import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  ArrowLeft,
  ArrowRight,
  Bold,
  ChevronLeft,
  ChevronRight,
  Circle,
  CircleDot,
  CornerDownRight,
  CopyPlus,
  Crop,
  Download,
  Eraser,
  FolderOpen,
  Grid3X3,
  Hand,
  Home,
  Italic,
  LineChart,
  Minus,
  MousePointer2,
  PenTool,
  Plus,
  Redo2,
  Save,
  Share2,
  Slash,
  Sigma,
  Trash2,
  Type,
  Undo2,
  Underline,
  X,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { convertLatexToMarkup } from 'mathlive';
import { InlineMathModal } from '../math/InlineMathField';
import { MathUtils } from '../math/MathUtils';
import ShareDocumentModal from '../document/ShareDocumentModal';
import { FirestoreService } from '../../services/firestore';
import { functions } from '../../services/firebase';
import { useAuthStore } from '../../stores/authStore';
import { canSaveToolWork, canShareContent, getToolSaveMessage } from '../../utils/subscription';

const CANVAS_WIDTH = 960;
const CANVAS_HEIGHT = 500;
const GRID_SIZE = 15;
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.1;
const COLOR_TABS = ['#000000', '#FFFFFF', '#2196F3', '#4CAF50', '#F44336', '#FF9800', '#9E9E9E'];
const DEFAULT_CANVAS_BACKGROUND_COLOR = '#FFFFFF';
const HEX_COLOR_PATTERN = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;
const RGB_COLOR_PATTERN = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i;
const PARAGRAPH_DEFAULT_WIDTH = 320;
const PARAGRAPH_DEFAULT_HEIGHT = 160;
const PARAGRAPH_MIN_WIDTH = 180;
const PARAGRAPH_MIN_HEIGHT = 96;
const PARAGRAPH_VIEWPORT_PADDING = 12;
const PARAGRAPH_TOOLBAR_HEIGHT = 44;
const PARAGRAPH_TOOLBAR_GAP = 8;

const TOOL_OPTIONS = [
  { id: 'pointer', label: 'Pointer', icon: MousePointer2 },
  { id: 'select', label: 'Select', icon: Crop },
  { id: 'pan', label: 'Pan', icon: Hand },
  { id: 'circle', label: 'Circle', icon: Circle },
  { id: 'line', label: 'Line', icon: Slash },
  { id: 'angle', label: 'Angle', icon: CornerDownRight },
  { id: 'point', label: 'Point', icon: CircleDot },
  { id: 'text', label: 'Text', icon: Type },
  { id: 'paragraph', label: 'Paragraph', icon: AlignLeft },
  { id: 'math', label: 'Math', icon: Sigma },
  { id: 'pen', label: 'Pen', icon: PenTool },
];

const CIRCLE_SHAPE_OPTIONS = [
  { id: 'circle', label: 'Circle' },
  { id: 'semicircle', label: 'Semicircle' },
];

const CIRCLE_MODE_OPTIONS = [
  { id: 'center-radius', label: 'Center drag' },
  { id: 'center-circumference', label: 'Center + point' },
  { id: 'three-points', label: '3 points' },
];

const SEMICIRCLE_MODE_OPTIONS = [
  { id: 'diameter-drag', label: 'Diameter drag' },
];

const LINE_STYLE_OPTIONS = [
  { id: 'solid', label: 'Solid', dash: [] },
  { id: 'dashed', label: 'Dashed', dash: [10, 6] },
  { id: 'dotted', label: 'Dotted', dash: [2, 6] },
];

const LINE_ARROW_OPTIONS = [
  { id: 'none', label: 'No Arrows' },
  { id: 'start', label: 'Start Only' },
  { id: 'end', label: 'End Only' },
  { id: 'both', label: 'Both Ends' },
];
const LINE_MARKING_OPTIONS = [
  { id: 'none', label: 'None' },
  { id: 'single', label: 'Single' },
  { id: 'double', label: 'Double' },
  { id: 'triple', label: 'Triple' },
];
const LINE_PARALLEL_MARKER_OPTIONS = [
  { id: 'none', label: 'None' },
  { id: 'single', label: 'Single' },
  { id: 'double', label: 'Double' },
  { id: 'triple', label: 'Triple' },
];
const STROKE_WEIGHT_OPTIONS = [
  { id: 1.5, label: 'Thin' },
  { id: 3, label: 'Medium' },
  { id: 4.5, label: 'Bold' },
];
const ANGLE_RADIUS_OPTIONS = [24, 36, 52, 72];
const ANGLE_SHADE_OPTIONS = [
  { id: 0.12, label: 'Light' },
  { id: 0.22, label: 'Medium' },
  { id: 0.34, label: 'Dark' },
];
const SELECTION_HANDLE_SIZE = 6;

const distToSegment = (point, a, b) => {
  const l2 = (b.x - a.x) ** 2 + (b.y - a.y) ** 2;
  if (!l2) return Math.hypot(point.x - a.x, point.y - a.y);
  let t = ((point.x - a.x) * (b.x - a.x) + (point.y - a.y) * (b.y - a.y)) / l2;
  t = Math.max(0, Math.min(1, t));
  const proj = { x: a.x + t * (b.x - a.x), y: a.y + t * (b.y - a.y) };
  return Math.hypot(point.x - proj.x, point.y - proj.y);
};

const distanceBetween = (a, b) => Math.hypot((b.x || 0) - (a.x || 0), (b.y || 0) - (a.y || 0));
const normalizeAngle = (angle) => {
  const fullTurn = Math.PI * 2;
  let nextAngle = angle % fullTurn;
  if (nextAngle < 0) nextAngle += fullTurn;
  return nextAngle;
};

const angleIsWithinSweep = (angle, startAngle, endAngle, anticlockwise = false) => {
  const fullTurn = Math.PI * 2;
  const normalizedAngle = normalizeAngle(angle);
  const normalizedStart = normalizeAngle(startAngle);
  const normalizedEnd = normalizeAngle(endAngle);

  if (!anticlockwise) {
    const sweep = (normalizedEnd - normalizedStart + fullTurn) % fullTurn;
    const offset = (normalizedAngle - normalizedStart + fullTurn) % fullTurn;
    return offset <= sweep + 1e-9;
  }

  const sweep = (normalizedStart - normalizedEnd + fullTurn) % fullTurn;
  const offset = (normalizedStart - normalizedAngle + fullTurn) % fullTurn;
  return offset <= sweep + 1e-9;
};

const normalizeRect = (rect) => ({
  x: Math.min(rect.x, rect.x + rect.width),
  y: Math.min(rect.y, rect.y + rect.height),
  width: Math.abs(rect.width),
  height: Math.abs(rect.height),
});

const pointInRect = (point, rect) =>
  point.x >= rect.x && point.x <= rect.x + rect.width && point.y >= rect.y && point.y <= rect.y + rect.height;

const IMAGE_MIN_SIZE = 24;
const LOCAL_DIAGRAMS_KEY = 'geometry-tool-diagrams';
const DEFAULT_DIAGRAM_TITLE = 'Untitled diagram';
const DEFAULT_CANVAS_TITLE_PREFIX = 'Slide';

const createDiagramId = () => `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const createCanvasSlideId = () => `geometry-canvas-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const getCanvasSlideTitle = (index) => `${DEFAULT_CANVAS_TITLE_PREFIX} [${index}]`;

const getImageBounds = (shape) => ({
  x: shape.x,
  y: shape.y,
  width: shape.width,
  height: shape.height,
});

const getMarkupBounds = (shape) => ({
  x: shape.x,
  y: shape.y,
  width: shape.width,
  height: shape.height,
});

const offsetShape = (shape, deltaX, deltaY) => {
  const nextShape = cloneShapes([shape])[0];

  if (nextShape.start) {
    nextShape.start.x += deltaX;
    nextShape.start.y += deltaY;
  }
  if (nextShape.end) {
    nextShape.end.x += deltaX;
    nextShape.end.y += deltaY;
  }
  if (nextShape.vertex) {
    nextShape.vertex.x += deltaX;
    nextShape.vertex.y += deltaY;
  }
  if (nextShape.pos) {
    nextShape.pos.x += deltaX;
    nextShape.pos.y += deltaY;
  }
  if (nextShape.path) {
    nextShape.path = nextShape.path.map((point) => ({ x: point.x + deltaX, y: point.y + deltaY }));
  }
  if (nextShape.type === 'circle' && nextShape.center) {
    nextShape.center.x += deltaX;
    nextShape.center.y += deltaY;
    if (Array.isArray(nextShape.sourcePoints)) {
      nextShape.sourcePoints = nextShape.sourcePoints.map((point) => ({
        x: point.x + deltaX,
        y: point.y + deltaY,
      }));
    }
  }
  if (nextShape.type === 'angle' && Array.isArray(nextShape.sourcePoints)) {
    nextShape.sourcePoints = nextShape.sourcePoints.map((point) => ({
      x: point.x + deltaX,
      y: point.y + deltaY,
    }));
  }
  if (nextShape.type === 'math' || nextShape.type === 'paragraph' || nextShape.type === 'image') {
    nextShape.x += deltaX;
    nextShape.y += deltaY;
  }

  return nextShape;
};

const isParagraphContentEmpty = (html = '') => {
  if (typeof document === 'undefined') {
    return !String(html || '').replace(/<[^>]*>/g, '').trim();
  }

  const container = document.createElement('div');
  container.innerHTML = html;
  const text = (container.textContent || '').replace(/\u200B/g, '').trim();
  return !text && !container.querySelector('.math-container[data-math-content]');
};

const PARAGRAPH_BLOCK_TAGS = new Set(['P', 'DIV', 'LI', 'UL', 'OL']);

const getParagraphPlainText = (html = '') => {
  if (typeof document === 'undefined') {
    return String(html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }

  const container = document.createElement('div');
  container.innerHTML = html || '';

  container.querySelectorAll('.math-container[data-math-content]').forEach((node) => {
    const latex = node.getAttribute('data-math-content') || '';
    node.textContent = latex ? ` ${latex} ` : ' ';
  });

  const parts = [];
  const appendBreak = () => {
    if (!parts.length || parts[parts.length - 1] === '\n') return;
    parts.push('\n');
  };

  const walk = (node) => {
    if (!node) return;

    if (node.nodeType === Node.TEXT_NODE) {
      const text = String(node.textContent || '').replace(/\u200B/g, '');
      if (text) parts.push(text);
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const tagName = node.tagName || '';
    if (tagName === 'BR') {
      appendBreak();
      return;
    }

    const isBlock = PARAGRAPH_BLOCK_TAGS.has(tagName);
    if (isBlock) appendBreak();
    Array.from(node.childNodes).forEach(walk);
    if (isBlock) appendBreak();
  };

  Array.from(container.childNodes).forEach(walk);

  return parts
    .join('')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const encodeSvg = (svg) => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
const CSS_URL_PATTERN = /url\(\s*(['"]?)([^'")]+)\1\s*\)/g;

const readBlobAsDataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});

const getCircleGeometry = (shape) => {
  if (!shape || shape.type !== 'circle') return null;
  if (shape.center && Number.isFinite(shape.radius)) {
    return {
      center: clonePoint(shape.center),
      radius: Math.max(0, shape.radius),
    };
  }

  if (shape.start && shape.end) {
    return {
      center: clonePoint(shape.start),
      radius: distanceBetween(shape.start, shape.end),
    };
  }

  return null;
};

const getSemicircleGeometry = (shape) => {
  if (!shape || shape.type !== 'semicircle' || !shape.start || !shape.end) return null;

  const diameter = distanceBetween(shape.start, shape.end);
  if (diameter <= 1e-9) return null;

  const center = {
    x: (shape.start.x + shape.end.x) / 2,
    y: (shape.start.y + shape.end.y) / 2,
  };
  const startAngle = Math.atan2(shape.start.y - center.y, shape.start.x - center.x);

  return {
    center,
    radius: diameter / 2,
    startAngle,
    endAngle: startAngle + Math.PI,
    anticlockwise: false,
  };
};

const getArcBounds = ({ center, radius, startAngle, endAngle, anticlockwise = false }) => {
  if (!center || !Number.isFinite(radius) || radius <= 0) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  const candidateAngles = [startAngle, endAngle, 0, Math.PI / 2, Math.PI, (3 * Math.PI) / 2]
    .filter((angle, index, values) => values.findIndex((value) => Math.abs(value - angle) <= 1e-9) === index)
    .filter((angle, index) => index < 2 || angleIsWithinSweep(angle, startAngle, endAngle, anticlockwise));

  const points = candidateAngles.map((angle) => ({
    x: center.x + (Math.cos(angle) * radius),
    y: center.y + (Math.sin(angle) * radius),
  }));

  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);

  return {
    x: Math.min(...xs),
    y: Math.min(...ys),
    width: Math.max(...xs) - Math.min(...xs),
    height: Math.max(...ys) - Math.min(...ys),
  };
};

const circumcircleFromPoints = (a, b, c) => {
  const d = 2 * (
    a.x * (b.y - c.y) +
    b.x * (c.y - a.y) +
    c.x * (a.y - b.y)
  );

  if (Math.abs(d) < 1e-9) return null;

  const ux = (
    ((a.x ** 2) + (a.y ** 2)) * (b.y - c.y) +
    ((b.x ** 2) + (b.y ** 2)) * (c.y - a.y) +
    ((c.x ** 2) + (c.y ** 2)) * (a.y - b.y)
  ) / d;
  const uy = (
    ((a.x ** 2) + (a.y ** 2)) * (c.x - b.x) +
    ((b.x ** 2) + (b.y ** 2)) * (a.x - c.x) +
    ((c.x ** 2) + (c.y ** 2)) * (b.x - a.x)
  ) / d;

  const center = { x: ux, y: uy };
  return {
    center,
    radius: distanceBetween(center, a),
  };
};

const getAngleGeometry = (shape) => {
  if (!shape || shape.type !== 'angle' || !shape.vertex || !shape.start || !shape.end) return null;

  const startDistance = distanceBetween(shape.vertex, shape.start);
  const endDistance = distanceBetween(shape.vertex, shape.end);
  if (startDistance <= 1e-9 || endDistance <= 1e-9) return null;

  const radius = Math.max(8, Number.isFinite(shape.radius) ? shape.radius : Math.min(startDistance, endDistance, 52));
  const startAngle = Math.atan2(shape.start.y - shape.vertex.y, shape.start.x - shape.vertex.x);
  const endAngle = Math.atan2(shape.end.y - shape.vertex.y, shape.end.x - shape.vertex.x);
  const clockwiseSweep = (normalizeAngle(endAngle - startAngle) + (Math.PI * 2)) % (Math.PI * 2);
  const useReflex = Boolean(shape.useReflex);
  const anticlockwise = useReflex ? clockwiseSweep <= Math.PI : clockwiseSweep > Math.PI;

  return {
    center: clonePoint(shape.vertex),
    radius,
    startAngle,
    endAngle,
    anticlockwise,
  };
};

const getAngleDraftShape = (shape) => {
  if (!shape || shape.type !== 'angle' || !Array.isArray(shape.sourcePoints)) return null;
  const points = [...shape.sourcePoints, ...(shape.previewPoint ? [shape.previewPoint] : [])];
  if (points.length < 3) return null;

  const radius = Math.max(18, Math.min(distanceBetween(points[1], points[0]), distanceBetween(points[1], points[2]), 52));
  return {
    ...shape,
    start: clonePoint(points[0]),
    vertex: clonePoint(points[1]),
    end: clonePoint(points[2]),
    radius,
  };
};

const drawAngleIndicator = (ctx, shape, zoom) => {
  const renderShape = getAngleDraftShape(shape) || shape;
  const geometry = getAngleGeometry(renderShape);
  if (!geometry) return;

  const startPoint = {
    x: geometry.center.x + Math.cos(geometry.startAngle) * geometry.radius,
    y: geometry.center.y + Math.sin(geometry.startAngle) * geometry.radius,
  };
  const endPoint = {
    x: geometry.center.x + Math.cos(geometry.endAngle) * geometry.radius,
    y: geometry.center.y + Math.sin(geometry.endAngle) * geometry.radius,
  };

  ctx.save();
  ctx.globalAlpha = Math.max(0.05, Math.min(0.7, renderShape.shade ?? 0.18));
  ctx.beginPath();
  ctx.moveTo(geometry.center.x, geometry.center.y);
  ctx.lineTo(startPoint.x, startPoint.y);
  ctx.arc(geometry.center.x, geometry.center.y, geometry.radius, geometry.startAngle, geometry.endAngle, geometry.anticlockwise);
  ctx.lineTo(geometry.center.x, geometry.center.y);
  ctx.closePath();
  ctx.fill();
  ctx.restore();

  ctx.beginPath();
  ctx.moveTo(startPoint.x, startPoint.y);
  ctx.arc(geometry.center.x, geometry.center.y, geometry.radius, geometry.startAngle, geometry.endAngle, geometry.anticlockwise);
  ctx.stroke();

  ctx.save();
  ctx.lineWidth = 1 / zoom;
  ctx.setLineDash([5 / zoom, 4 / zoom]);
  ctx.globalAlpha = 0.5;
  ctx.beginPath();
  ctx.moveTo(geometry.center.x, geometry.center.y);
  ctx.lineTo(startPoint.x, startPoint.y);
  ctx.moveTo(geometry.center.x, geometry.center.y);
  ctx.lineTo(endPoint.x, endPoint.y);
  ctx.stroke();
  ctx.restore();
};

const getShapeBounds = (shape, ctx) => {
  if (shape.type === 'line') {
    return {
      x: Math.min(shape.start.x, shape.end.x),
      y: Math.min(shape.start.y, shape.end.y),
      width: Math.abs(shape.end.x - shape.start.x),
      height: Math.abs(shape.end.y - shape.start.y),
    };
  }
  if (shape.type === 'circle') {
    const geometry = getCircleGeometry(shape);
    const radius = geometry?.radius || 0;
    const center = geometry?.center || { x: 0, y: 0 };
    return {
      x: center.x - radius,
      y: center.y - radius,
      width: radius * 2,
      height: radius * 2,
    };
  }
  if (shape.type === 'semicircle') {
    const geometry = getSemicircleGeometry(shape);
    return geometry ? getArcBounds(geometry) : { x: 0, y: 0, width: 0, height: 0 };
  }
  if (shape.type === 'angle') {
    const geometry = getAngleGeometry(shape);
    if (!geometry) return { x: shape.vertex?.x || 0, y: shape.vertex?.y || 0, width: 0, height: 0 };
    const bounds = getArcBounds(geometry);
    const left = Math.min(bounds.x, shape.vertex.x);
    const top = Math.min(bounds.y, shape.vertex.y);
    const right = Math.max(bounds.x + bounds.width, shape.vertex.x);
    const bottom = Math.max(bounds.y + bounds.height, shape.vertex.y);
    return { x: left, y: top, width: right - left, height: bottom - top };
  }
  if (shape.type === 'point') {
    return { x: shape.pos.x - 4, y: shape.pos.y - 4, width: 8, height: 8 };
  }
  if (shape.type === 'text') {
    ctx.save();
    ctx.font = `${shape.size}px Arial`;
    const width = ctx.measureText(shape.text).width;
    ctx.restore();
    return { x: shape.pos.x - 2, y: shape.pos.y - shape.size, width: width + 4, height: shape.size + 4 };
  }
  if (shape.type === 'pen') {
    const xs = shape.path.map((p) => p.x);
    const ys = shape.path.map((p) => p.y);
    return {
      x: Math.min(...xs),
      y: Math.min(...ys),
      width: Math.max(...xs) - Math.min(...xs),
      height: Math.max(...ys) - Math.min(...ys),
    };
  }
  if (shape.type === 'image') {
    return getImageBounds(shape);
  }
  if (shape.type === 'math') {
    return getMarkupBounds(shape);
  }
  if (shape.type === 'paragraph') {
    return getMarkupBounds(shape);
  }
  return { x: 0, y: 0, width: 0, height: 0 };
};

const getImageResizeHandles = (shape) => {
  const bounds = getImageBounds(shape);
  return {
    nw: { x: bounds.x, y: bounds.y },
    ne: { x: bounds.x + bounds.width, y: bounds.y },
    sw: { x: bounds.x, y: bounds.y + bounds.height },
    se: { x: bounds.x + bounds.width, y: bounds.y + bounds.height },
  };
};

const getParagraphResizeHandles = (shape) => {
  const bounds = getMarkupBounds(shape);
  return {
    left: { x: bounds.x, y: bounds.y + (bounds.height / 2) },
    right: { x: bounds.x + bounds.width, y: bounds.y + (bounds.height / 2) },
  };
};

const cloneShapes = (shapes) => JSON.parse(JSON.stringify(shapes));
const clonePoint = (point) => ({ x: point.x, y: point.y });

const getSelectionBoundsFromShapes = (shapes, indices, ctx) => {
  if (!ctx || !Array.isArray(indices) || !indices.length) return null;
  const boundsList = indices
    .map((index) => (shapes[index] ? getShapeBounds(shapes[index], ctx) : null))
    .filter(Boolean);
  if (!boundsList.length) return null;

  const left = Math.min(...boundsList.map((bounds) => bounds.x));
  const top = Math.min(...boundsList.map((bounds) => bounds.y));
  const right = Math.max(...boundsList.map((bounds) => bounds.x + bounds.width));
  const bottom = Math.max(...boundsList.map((bounds) => bounds.y + bounds.height));

  return {
    x: left,
    y: top,
    width: right - left,
    height: bottom - top,
  };
};

const getSelectionHandles = (bounds) => ({
  nw: { x: bounds.x, y: bounds.y },
  ne: { x: bounds.x + bounds.width, y: bounds.y },
  sw: { x: bounds.x, y: bounds.y + bounds.height },
  se: { x: bounds.x + bounds.width, y: bounds.y + bounds.height },
});

const scalePointWithinBounds = (point, fromBounds, toBounds) => ({
  x: toBounds.x + ((point.x - fromBounds.x) / Math.max(fromBounds.width, 1e-9)) * toBounds.width,
  y: toBounds.y + ((point.y - fromBounds.y) / Math.max(fromBounds.height, 1e-9)) * toBounds.height,
});

const normalizeHexColor = (value) => {
  const trimmed = String(value || '').trim();
  if (!HEX_COLOR_PATTERN.test(trimmed)) return null;
  if (trimmed.length === 4) {
    return `#${trimmed[1]}${trimmed[1]}${trimmed[2]}${trimmed[2]}${trimmed[3]}${trimmed[3]}`.toUpperCase();
  }
  return trimmed.toUpperCase();
};

const normalizeRgbColor = (value) => {
  const match = String(value || '').trim().match(RGB_COLOR_PATTERN);
  if (!match) return null;
  const [red, green, blue] = match.slice(1, 4).map(Number);
  if ([red, green, blue].some((channel) => !Number.isInteger(channel) || channel < 0 || channel > 255)) return null;
  const alpha = match[4];
  if (alpha !== undefined) {
    const parsedAlpha = Number(alpha);
    if (!Number.isFinite(parsedAlpha) || parsedAlpha < 0 || parsedAlpha > 1) return null;
    return `rgba(${red}, ${green}, ${blue}, ${parsedAlpha})`;
  }
  return `rgb(${red}, ${green}, ${blue})`;
};

const normalizeCanvasBackgroundColor = (value) => {
  const trimmed = String(value || '').trim();
  return normalizeHexColor(trimmed) || normalizeRgbColor(trimmed) || DEFAULT_CANVAS_BACKGROUND_COLOR;
};

const isValidCanvasBackgroundColor = (value) =>
  Boolean(normalizeHexColor(value) || normalizeRgbColor(value));

const getColorInputValue = (value) =>
  normalizeHexColor(value) || DEFAULT_CANVAS_BACKGROUND_COLOR;

const normalizeDiagramCanvasState = (diagramState = {}) => {
  const nextPan = diagramState?.pan && Number.isFinite(diagramState.pan.x) && Number.isFinite(diagramState.pan.y)
    ? clonePoint(diagramState.pan)
    : { x: CANVAS_WIDTH / 2, y: CANVAS_HEIGHT / 2 };

  return {
    shapes: Array.isArray(diagramState?.shapes) ? cloneShapes(diagramState.shapes) : [],
    currentColor: typeof diagramState?.currentColor === 'string' ? diagramState.currentColor : '#000000',
    currentTextSize: [16, 21, 28].includes(diagramState?.currentTextSize) ? diagramState.currentTextSize : 21,
    showGrid: Boolean(diagramState?.showGrid),
    showAxes: Boolean(diagramState?.showAxes),
    canvasBackgroundColor: normalizeCanvasBackgroundColor(diagramState?.canvasBackgroundColor),
    zoom: Number.isFinite(diagramState?.zoom) ? Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, diagramState.zoom)) : 1,
    pan: nextPan,
  };
};

const createCanvasSlide = (index, state = {}) => ({
  id: createCanvasSlideId(),
  title: getCanvasSlideTitle(index),
  state: normalizeDiagramCanvasState(state),
});

const renumberCanvasSlides = (slides = []) => slides.map((slide, index) => ({
  ...slide,
  id: slide?.id || createCanvasSlideId(),
  title: getCanvasSlideTitle(index + 1),
}));

const readLocalDiagrams = () => {
  if (typeof window === 'undefined') return [];

  try {
    const raw = window.localStorage.getItem(LOCAL_DIAGRAMS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeLocalDiagrams = (diagrams) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(LOCAL_DIAGRAMS_KEY, JSON.stringify(diagrams));
};

const drawArrowHead = (ctx, from, to, zoom) => {
  const angle = Math.atan2(to.y - from.y, to.x - from.x);
  const length = 14 / zoom;
  const spread = Math.PI / 7;

  ctx.beginPath();
  ctx.moveTo(to.x, to.y);
  ctx.lineTo(
    to.x - length * Math.cos(angle - spread),
    to.y - length * Math.sin(angle - spread),
  );
  ctx.moveTo(to.x, to.y);
  ctx.lineTo(
    to.x - length * Math.cos(angle + spread),
    to.y - length * Math.sin(angle + spread),
  );
  ctx.stroke();
};

const drawLineMarkings = (ctx, start, end, marking, zoom) => {
  const countMap = {
    single: 1,
    double: 2,
    triple: 3,
  };
  const count = countMap[marking] || 0;
  if (!count) return;

  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.hypot(dx, dy);
  if (length < 1e-6) return;

  const ux = dx / length;
  const uy = dy / length;
  const px = -uy;
  const py = ux;
  const tickLength = 14 / zoom;
  const spacing = 10 / zoom;
  const halfTick = tickLength / 2;
  const centerX = (start.x + end.x) / 2;
  const centerY = (start.y + end.y) / 2;
  const offsetBase = ((count - 1) / 2) * spacing;

  ctx.beginPath();
  for (let index = 0; index < count; index += 1) {
    const offset = (index * spacing) - offsetBase;
    const midX = centerX + (ux * offset);
    const midY = centerY + (uy * offset);
    ctx.moveTo(midX - (px * halfTick), midY - (py * halfTick));
    ctx.lineTo(midX + (px * halfTick), midY + (py * halfTick));
  }
  ctx.stroke();
};

const drawParallelLineMarkers = (ctx, start, end, marker, zoom) => {
  const countMap = {
    single: 1,
    double: 2,
    triple: 3,
  };
  const count = countMap[marker] || 0;
  if (!count) return;

  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.hypot(dx, dy);
  if (length < 1e-6) return;

  const ux = dx / length;
  const uy = dy / length;
  const centerX = (start.x + end.x) / 2;
  const centerY = (start.y + end.y) / 2;
  const markerLength = 12 / zoom;
  const spacing = 12 / zoom;
  const spread = 8 / zoom;
  const offsetBase = ((count - 1) / 2) * spacing;

  ctx.beginPath();
  for (let index = 0; index < count; index += 1) {
    const offset = (index * spacing) - offsetBase;
    const apexX = centerX + (ux * offset);
    const apexY = centerY + (uy * offset);
    const baseX = apexX - (ux * markerLength);
    const baseY = apexY - (uy * markerLength);

    ctx.moveTo(baseX + (-uy * spread), baseY + (ux * spread));
    ctx.lineTo(apexX, apexY);
    ctx.lineTo(baseX - (-uy * spread), baseY - (ux * spread));
  }
  ctx.stroke();
};

function GeometryTool({
  fullScreen = false,
  showSaveBar = true,
  openLibrarySignal = 0,
  newCanvasSignal = 0,
  readOnly = false,
  allowTeachingModeInReadOnly = false,
  initialDiagramData = null,
  initialTitle = '',
}) {
  const user = useAuthStore((state) => state.user);
  const userData = useAuthStore((state) => state.userData);
  const initialSlideDeck = useMemo(() => {
    const firstSlide = createCanvasSlide(1);
    return {
      slides: [firstSlide],
      activeSlideId: firstSlide.id,
    };
  }, []);
  const fullScreenRootRef = useRef(null);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const circleMenuRef = useRef(null);
  const textInputRef = useRef(null);
  const paragraphEditorRef = useRef(null);
  const paragraphToolbarRef = useRef(null);
  const paragraphSelectionRef = useRef(null);
  const paragraphDisplayRefs = useRef(new Map());
  const paragraphOutsideCloseReadyRef = useRef(false);
  const imageCacheRef = useRef(new Map());
  const mathCacheRef = useRef(new Map());
  const mathCssTextPromiseRef = useRef(null);
  const mathFontDataUrlCacheRef = useRef(new Map());
  const paragraphAssetCacheRef = useRef(new Map());
  const redrawRef = useRef(null);

  const [currentTool, setCurrentTool] = useState('pointer');
  const [shapes, setShapes] = useState([]);
  const [selectedIndices, setSelectedIndices] = useState([]);
  const [history, setHistory] = useState([]);
  const [future, setFuture] = useState([]);
  const [currentColor, setCurrentColor] = useState('#000000');
  const [currentTextSize, setCurrentTextSize] = useState(21);
  const [circleShape, setCircleShape] = useState('circle');
  const [circleMode, setCircleMode] = useState('center-radius');
  const [isCircleMenuOpen, setIsCircleMenuOpen] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [showAxes, setShowAxes] = useState(false);
  const [canvasBackgroundColor, setCanvasBackgroundColor] = useState(DEFAULT_CANVAS_BACKGROUND_COLOR);
  const [canvasBackgroundInput, setCanvasBackgroundInput] = useState(DEFAULT_CANVAS_BACKGROUND_COLOR);
  const [canvasSize, setCanvasSize] = useState({ width: CANVAS_WIDTH, height: CANVAS_HEIGHT });
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: CANVAS_WIDTH / 2, y: CANVAS_HEIGHT / 2 });
  const [draftShape, setDraftShape] = useState(null);
  const [selectionRect, setSelectionRect] = useState(null);
  const [textDraft, setTextDraft] = useState(null);
  const [mathDraft, setMathDraft] = useState(null);
  const [paragraphDraft, setParagraphDraft] = useState(null);
  const [paragraphMathDraft, setParagraphMathDraft] = useState(null);
  const [hoverWorldPoint, setHoverWorldPoint] = useState(null);
  const [teachingMode, setTeachingMode] = useState(false);
  const [slides, setSlides] = useState(initialSlideDeck.slides);
  const [activeSlideId, setActiveSlideId] = useState(initialSlideDeck.activeSlideId);
  const [diagramTitle, setDiagramTitle] = useState(DEFAULT_DIAGRAM_TITLE);
  const [currentDiagramId, setCurrentDiagramId] = useState('');
  const [savedDiagrams, setSavedDiagrams] = useState([]);
  const [sharedDiagrams, setSharedDiagrams] = useState([]);
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [isLibraryLoading, setIsLibraryLoading] = useState(false);
  const [isSavingDiagram, setIsSavingDiagram] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [pdfHandoutLayout, setPdfHandoutLayout] = useState('portrait-2');
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [libraryTab, setLibraryTab] = useState('saved');
  const [saveFeedback, setSaveFeedback] = useState({ type: 'idle', message: '' });
  const isFullscreenMode = fullScreen || teachingMode;
  const safeShapes = Array.isArray(shapes) ? shapes : [];
  const safeSelectedIndices = Array.isArray(selectedIndices) ? selectedIndices : [];
  const safeSlides = Array.isArray(slides) ? slides : [];
  const safeSavedDiagrams = Array.isArray(savedDiagrams) ? savedDiagrams : [];
  const safeSharedDiagrams = Array.isArray(sharedDiagrams) ? sharedDiagrams : [];
  const editingParagraphKey = paragraphDraft ? (paragraphDraft.shapeIndex ?? -1) : null;

  const selectedLineIndex = useMemo(() => {
    if (safeSelectedIndices.length !== 1) return -1;
    const index = safeSelectedIndices[0];
    return safeShapes[index]?.type === 'line' ? index : -1;
  }, [safeSelectedIndices, safeShapes]);
  const selectedStrokeShapeIndex = useMemo(() => {
    if (safeSelectedIndices.length !== 1) return -1;
    const index = safeSelectedIndices[0];
    const type = safeShapes[index]?.type;
    return type === 'line' || type === 'circle' || type === 'semicircle' || type === 'angle' ? index : -1;
  }, [safeSelectedIndices, safeShapes]);
  const selectedAngleIndex = useMemo(() => {
    if (safeSelectedIndices.length !== 1) return -1;
    const index = safeSelectedIndices[0];
    return safeShapes[index]?.type === 'angle' ? index : -1;
  }, [safeSelectedIndices, safeShapes]);
  const activeSlide = useMemo(
    () => safeSlides.find((slide) => slide.id === activeSlideId) || safeSlides[0] || null,
    [activeSlideId, safeSlides]
  );
  const activeSlideIndex = useMemo(
    () => safeSlides.findIndex((slide) => slide.id === activeSlideId),
    [activeSlideId, safeSlides]
  );

  const interactionRef = useRef({
    mode: null,
    start: null,
    startPan: null,
    startClient: null,
    initialShapes: null,
    selectedShapeStart: null,
    draggedLineHandle: null,
    draggedImageHandle: null,
    draggedParagraphHandle: null,
    selectionBoundsStart: null,
    selectionResizeHandle: null,
    selectionResizeAnchor: null,
  });

  const applyCanvasState = useCallback((diagramState, options = {}) => {
    const { preserveTeachingMode = false } = options;
    const nextState = normalizeDiagramCanvasState(diagramState);

    setShapes(nextState.shapes);
    setSelectedIndices([]);
    setHistory([]);
    setFuture([]);
    setDraftShape(null);
    setSelectionRect(null);
    setTextDraft(null);
    setMathDraft(null);
    setParagraphDraft(null);
    setParagraphMathDraft(null);
    setCurrentTool('pointer');
    if (!preserveTeachingMode) setTeachingMode(false);
    setCurrentColor(nextState.currentColor);
    setCurrentTextSize(nextState.currentTextSize);
    setShowGrid(nextState.showGrid);
    setShowAxes(nextState.showAxes);
    setCanvasBackgroundColor(nextState.canvasBackgroundColor);
    setCanvasBackgroundInput(nextState.canvasBackgroundColor);
    setZoom(nextState.zoom);
    setPan(nextState.pan);
  }, []);

  const buildCanvasState = useCallback(() => ({
    shapes: cloneShapes(shapes),
    currentColor,
    currentTextSize,
    showGrid,
    showAxes,
    canvasBackgroundColor,
    zoom,
    pan: clonePoint(pan),
  }), [canvasBackgroundColor, currentColor, currentTextSize, pan, shapes, showAxes, showGrid, zoom]);

  const buildSlidesSnapshot = useCallback(() => {
    const currentCanvasState = buildCanvasState();
    const nextSlides = renumberCanvasSlides((slides.length ? slides : [createCanvasSlide(1)]).map((slide, index) => ({
      id: slide?.id || createCanvasSlideId(),
      title: slide?.title?.trim() || getCanvasSlideTitle(index + 1),
      state: slide?.id === activeSlideId
        ? currentCanvasState
        : normalizeDiagramCanvasState(slide?.state || slide),
    })));
    const nextActiveSlideId = nextSlides.some((slide) => slide.id === activeSlideId)
      ? activeSlideId
      : nextSlides[0]?.id || '';
    return {
      slides: nextSlides,
      activeSlideId: nextActiveSlideId,
      currentCanvasState,
    };
  }, [activeSlideId, buildCanvasState, slides]);

  const applyDiagramState = useCallback((diagramState) => {
    const rawSlides = Array.isArray(diagramState?.slides) && diagramState.slides.length
      ? diagramState.slides
      : [{ id: createCanvasSlideId(), title: getCanvasSlideTitle(1), state: diagramState }];
    const normalizedSlides = renumberCanvasSlides(rawSlides.map((slide, index) => ({
      id: slide?.id || createCanvasSlideId(),
      title: slide?.title?.trim() || getCanvasSlideTitle(index + 1),
      state: normalizeDiagramCanvasState(slide?.state || slide),
    })));
    const nextActiveSlideId = normalizedSlides.some((slide) => slide.id === diagramState?.activeSlideId)
      ? diagramState.activeSlideId
      : normalizedSlides[0]?.id || '';
    const nextActiveSlide = normalizedSlides.find((slide) => slide.id === nextActiveSlideId) || normalizedSlides[0];

    setSlides(normalizedSlides);
    setActiveSlideId(nextActiveSlideId);
    applyCanvasState(nextActiveSlide?.state || normalizeDiagramCanvasState());
  }, [applyCanvasState]);

  const serializeDiagramState = useCallback(() => {
    const { slides: nextSlides, activeSlideId: nextActiveSlideId, currentCanvasState } = buildSlidesSnapshot();
    return {
      version: 2,
      ...currentCanvasState,
      slides: cloneShapes(nextSlides),
      activeSlideId: nextActiveSlideId,
    };
  }, [buildSlidesSnapshot]);

  const startNewCanvas = useCallback(() => {
    const firstSlide = createCanvasSlide(1);
    setSlides([firstSlide]);
    setActiveSlideId(firstSlide.id);
    applyCanvasState(firstSlide.state);
    setCurrentDiagramId('');
    setDiagramTitle(DEFAULT_DIAGRAM_TITLE);
    setIsLibraryOpen(false);
    setSaveFeedback({ type: 'idle', message: '' });
  }, [applyCanvasState]);

  const loadSavedDiagrams = useCallback(async () => {
    try {
      if (user?.uid) {
        const diagrams = await FirestoreService.getUserGeometryDiagrams(user.uid);
        setSavedDiagrams(diagrams.map((diagram) => ({
          id: diagram.id,
          title: diagram.title || DEFAULT_DIAGRAM_TITLE,
          updatedAt: diagram.updatedAt?.toDate?.() || new Date(),
          storage: 'cloud',
        })));
      } else {
        const diagrams = readLocalDiagrams()
          .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))
          .map((diagram) => ({
            id: diagram.id,
            title: diagram.title || DEFAULT_DIAGRAM_TITLE,
            updatedAt: new Date(diagram.updatedAt || Date.now()),
            storage: 'local',
          }));
        setSavedDiagrams(diagrams);
      }
    } catch (error) {
      console.error('Failed to load saved diagrams:', error);
      setSaveFeedback({ type: 'error', message: error.message || 'Failed to load saved diagrams.' });
      setSavedDiagrams([]);
    }
  }, [user?.uid]);

  const loadSharedDiagrams = useCallback(async () => {
    if (!user?.uid) {
      setSharedDiagrams([]);
      return;
    }

    try {
      const shares = await FirestoreService.getSharesForRecipient(user.uid);
      const geometryShares = shares
        .filter((share) => share.shareType === 'user')
        .filter((share) => share.documentContentType === 'geometry-diagram' || share.toolType === 'geometry-tool');

      setSharedDiagrams(geometryShares.map((share) => ({
        id: share.id,
        title: share.documentName || DEFAULT_DIAGRAM_TITLE,
        ownerName: share.ownerName || share.ownerEmail || 'Unknown',
        updatedAt: share.createdAt?.toDate?.() || new Date(),
        linkToken: share.linkToken || '',
        documentId: share.documentId,
      })));
    } catch (error) {
      console.error('Failed to load shared diagrams:', error);
      setSaveFeedback({ type: 'error', message: error.message || 'Failed to load shared diagrams.' });
      setSharedDiagrams([]);
    }
  }, [user?.uid]);

  const loadDiagram = useCallback(async (diagramId) => {
    try {
      if (user?.uid) {
        const diagram = await FirestoreService.getGeometryDiagram(diagramId);
        applyDiagramState(diagram.diagramData || {});
        setCurrentDiagramId(diagram.id);
        setDiagramTitle(diagram.title || DEFAULT_DIAGRAM_TITLE);
      } else {
        const diagram = readLocalDiagrams().find((item) => item.id === diagramId);
        if (!diagram) throw new Error('Saved diagram not found.');
        applyDiagramState(diagram.diagramData || {});
        setCurrentDiagramId(diagram.id);
        setDiagramTitle(diagram.title || DEFAULT_DIAGRAM_TITLE);
      }

      setSaveFeedback({ type: 'saved', message: 'Diagram loaded.' });
      setIsLibraryOpen(false);
    } catch (error) {
      console.error('Failed to load diagram:', error);
      setSaveFeedback({ type: 'error', message: error.message || 'Failed to load diagram.' });
    }
  }, [applyDiagramState, user?.uid]);

  const deleteDiagram = useCallback(async (diagramId) => {
    try {
      if (user?.uid) {
        await FirestoreService.deleteGeometryDiagram(diagramId);
      } else {
        const nextLocalDiagrams = readLocalDiagrams().filter((diagram) => diagram.id !== diagramId);
        writeLocalDiagrams(nextLocalDiagrams);
      }

      if (currentDiagramId === diagramId) {
        setCurrentDiagramId('');
        setDiagramTitle(DEFAULT_DIAGRAM_TITLE);
      }

      await loadSavedDiagrams();
      setSaveFeedback({ type: 'saved', message: 'Diagram deleted.' });
    } catch (error) {
      console.error('Failed to delete diagram:', error);
      setSaveFeedback({ type: 'error', message: error.message || 'Failed to delete diagram.' });
    }
  }, [currentDiagramId, loadSavedDiagrams, user?.uid]);

  const saveDiagram = useCallback(async (options = {}) => {
    const { saveAsNew = false } = options;
    const nextTitle = (diagramTitle || '').trim() || DEFAULT_DIAGRAM_TITLE;
    const diagramData = serializeDiagramState();

    if (!canSaveToolWork(userData)) {
      setSaveFeedback({ type: 'error', message: getToolSaveMessage() });
      return;
    }

    setIsSavingDiagram(true);
    setSaveFeedback({ type: 'saving', message: 'Saving diagram...' });

    try {
      if (user?.uid) {
        if (!saveAsNew && currentDiagramId) {
          await FirestoreService.updateGeometryDiagram(currentDiagramId, {
            title: nextTitle,
            status: 'draft',
            diagramData,
          });
        } else {
          const createdId = await FirestoreService.createGeometryDiagram(user.uid, {
            title: nextTitle,
            status: 'draft',
            diagramData,
          });
          setCurrentDiagramId(createdId);
        }
      } else {
        const diagrams = readLocalDiagrams();
        const diagramId = !saveAsNew && currentDiagramId ? currentDiagramId : createDiagramId();
        const createdAt = (!saveAsNew && currentDiagramId
          ? diagrams.find((diagram) => diagram.id === diagramId)?.createdAt
          : null) || new Date().toISOString();
        const nextDiagram = {
          id: diagramId,
          title: nextTitle,
          diagramData,
          createdAt,
          updatedAt: new Date().toISOString(),
        };
        const remaining = diagrams.filter((diagram) => diagram.id !== diagramId);
        writeLocalDiagrams([nextDiagram, ...remaining]);
        setCurrentDiagramId(diagramId);
      }

      setDiagramTitle(nextTitle);
      setSaveFeedback({
        type: 'saved',
        message: user?.uid ? 'Diagram saved to your account.' : 'Diagram saved in this browser.',
      });
      await loadSavedDiagrams();
    } catch (error) {
      console.error('Failed to save diagram:', error);
      setSaveFeedback({ type: 'error', message: error.message || 'Failed to save diagram.' });
    } finally {
      setIsSavingDiagram(false);
    }
  }, [currentDiagramId, diagramTitle, loadSavedDiagrams, serializeDiagramState, user?.uid, userData]);

  const switchToSlide = useCallback((slideId) => {
    if (!slideId || slideId === activeSlideId) return;
    const { slides: nextSlides } = buildSlidesSnapshot();
    const targetSlide = nextSlides.find((slide) => slide.id === slideId);
    if (!targetSlide) return;
    setSlides(nextSlides);
    setActiveSlideId(targetSlide.id);
    applyCanvasState(targetSlide.state, { preserveTeachingMode: true });
  }, [activeSlideId, applyCanvasState, buildSlidesSnapshot]);

  const addSlide = useCallback(() => {
    const { slides: nextSlides } = buildSlidesSnapshot();
    const nextSlide = createCanvasSlide(nextSlides.length + 1);
    setSlides(renumberCanvasSlides([...nextSlides, nextSlide]));
    setActiveSlideId(nextSlide.id);
    applyCanvasState(nextSlide.state, { preserveTeachingMode: true });
  }, [applyCanvasState, buildSlidesSnapshot]);

  const duplicateSlide = useCallback(() => {
    const { slides: nextSlides, activeSlideId: nextActiveSlideId } = buildSlidesSnapshot();
    const sourceSlide = nextSlides.find((slide) => slide.id === nextActiveSlideId) || nextSlides[0];
    if (!sourceSlide) return;
    const duplicate = {
      id: createCanvasSlideId(),
      title: '',
      state: cloneShapes(sourceSlide.state),
    };
    setSlides(renumberCanvasSlides([...nextSlides, duplicate]));
    setActiveSlideId(duplicate.id);
    applyCanvasState(duplicate.state, { preserveTeachingMode: true });
  }, [applyCanvasState, buildSlidesSnapshot]);

  const moveSlide = useCallback((direction) => {
    const { slides: nextSlides, activeSlideId: nextActiveSlideId } = buildSlidesSnapshot();
    const currentIndex = nextSlides.findIndex((slide) => slide.id === nextActiveSlideId);
    if (currentIndex < 0) return;

    const targetIndex = direction === 'backward' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= nextSlides.length) return;

    const reorderedSlides = [...nextSlides];
    const [movedSlide] = reorderedSlides.splice(currentIndex, 1);
    reorderedSlides.splice(targetIndex, 0, movedSlide);

    setSlides(renumberCanvasSlides(reorderedSlides));
    setActiveSlideId(movedSlide.id);
  }, [buildSlidesSnapshot]);

  const removeSlide = useCallback((slideId) => {
    const { slides: nextSlides, activeSlideId: nextActiveSlideId } = buildSlidesSnapshot();
    if (!nextSlides.length) return;

    if (nextSlides.length === 1) {
      const replacement = createCanvasSlide(1);
      setSlides(renumberCanvasSlides([replacement]));
      setActiveSlideId(replacement.id);
      applyCanvasState(replacement.state, { preserveTeachingMode: true });
      return;
    }

    const removedIndex = nextSlides.findIndex((slide) => slide.id === slideId);
    const remainingSlides = nextSlides.filter((slide) => slide.id !== slideId);
    const fallbackIndex = removedIndex > 0 ? removedIndex - 1 : 0;
    const nextSlide = slideId === nextActiveSlideId
      ? remainingSlides[fallbackIndex] || remainingSlides[0]
      : remainingSlides.find((slide) => slide.id === nextActiveSlideId) || remainingSlides[0];

    setSlides(renumberCanvasSlides(remainingSlides));
    if (nextSlide) {
      setActiveSlideId(nextSlide.id);
      applyCanvasState(nextSlide.state, { preserveTeachingMode: true });
    }
  }, [applyCanvasState, buildSlidesSnapshot]);

  const commitShapes = useCallback((nextShapes, snapshot = shapes) => {
    setHistory((prev) => [...prev, cloneShapes(snapshot)]);
    setFuture([]);
    setShapes(nextShapes);
  }, [shapes]);

  const saveParagraphSelection = useCallback(() => {
    const editor = paragraphEditorRef.current;
    const selection = window.getSelection?.();
    if (!editor || !selection || !selection.rangeCount) return;
    const range = selection.getRangeAt(0);
    if (editor.contains(range.commonAncestorContainer)) {
      paragraphSelectionRef.current = range.cloneRange();
    }
  }, []);

  const restoreParagraphSelection = useCallback(() => {
    const editor = paragraphEditorRef.current;
    const savedRange = paragraphSelectionRef.current;
    const selection = window.getSelection?.();
    if (!editor || !savedRange || !selection) return false;
    selection.removeAllRanges();
    selection.addRange(savedRange);
    return true;
  }, []);

  const resetParagraphMathContainers = useCallback((container) => {
    if (!container) return;
    const mathContainers = container.querySelectorAll('.math-container[data-math-content]');
    mathContainers.forEach((el) => {
      const latex = el.getAttribute('data-math-content');
      el.contentEditable = 'false';
      el.style.cursor = 'pointer';
      el.style.display = 'inline-block';
      el.style.verticalAlign = 'baseline';
      el.style.whiteSpace = 'nowrap';
      el.style.lineHeight = 'inherit';
      el.style.fontSize = 'inherit';
      el.innerHTML = `\\(${latex}\\)`;
    });
  }, []);

  const renderParagraphMath = useCallback(async (container) => {
    if (!container) return;
    resetParagraphMathContainers(container);
    const mathContainers = container.querySelectorAll('.math-container[data-math-content]');
    if (!mathContainers.length) return;

    try {
      const { renderMathInElement } = await import('mathlive');
      mathContainers.forEach((el) => {
        renderMathInElement(el, { renderAccessibleContent: false });
        el.style.display = 'inline-block';
        el.style.verticalAlign = 'baseline';
        el.style.lineHeight = 'inherit';
        el.style.fontSize = 'inherit';
        const renderedRoot = el.firstElementChild;
        if (renderedRoot) {
          renderedRoot.style.display = 'inline-block';
          renderedRoot.style.verticalAlign = 'baseline';
          renderedRoot.style.lineHeight = 'inherit';
          renderedRoot.style.fontSize = 'inherit';
        }
      });
    } catch (error) {
      console.warn('Paragraph math render failed, falling back to MathJax:', error);
      await MathUtils.renderMath(container);
      mathContainers.forEach((el) => {
        el.style.display = 'inline-block';
        el.style.verticalAlign = 'baseline';
        el.style.lineHeight = 'inherit';
        el.style.fontSize = 'inherit';
      });
    }
  }, [resetParagraphMathContainers]);

  const getParagraphViewportMetrics = useCallback(() => {
    const containerRect = containerRef.current?.getBoundingClientRect?.();
    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : canvasSize.width;
    const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : canvasSize.height;

    if (!containerRect) {
      return {
        width: canvasSize.width,
        height: canvasSize.height,
      };
    }

    return {
      width: Math.max(
        120,
        Math.min(containerRect.width, viewportWidth - containerRect.left) - (PARAGRAPH_VIEWPORT_PADDING * 2)
      ),
      height: Math.max(
        72,
        Math.min(containerRect.height, viewportHeight - containerRect.top) - (PARAGRAPH_VIEWPORT_PADDING * 2)
      ),
    };
  }, [canvasSize.height, canvasSize.width]);

  const getParagraphEditorMetrics = useCallback(() => {
    const { width: viewportWidth, height: viewportHeight } = getParagraphViewportMetrics();
    return {
      viewportWidth,
      viewportHeight,
      maxEditorWidth: viewportWidth,
      maxEditorHeight: Math.max(72, viewportHeight - PARAGRAPH_TOOLBAR_HEIGHT - PARAGRAPH_TOOLBAR_GAP),
    };
  }, [getParagraphViewportMetrics]);

  const syncParagraphDraftFromEditor = useCallback(() => {
    const editor = paragraphEditorRef.current;
    const canvasRect = canvasRef.current?.getBoundingClientRect?.();
    if (!editor || !canvasRect) return;

    const scaleX = canvasRect.width ? canvasSize.width / canvasRect.width : 1;
    const scaleY = canvasRect.height ? canvasSize.height / canvasRect.height : 1;
    const html = editor.innerHTML;
    const boxWidth = editor.offsetWidth || editor.clientWidth || 0;
    const contentHeight = Math.max(
      editor.scrollHeight || 0,
      editor.offsetHeight || 0,
      editor.clientHeight || 0
    );
    const width = boxWidth * scaleX / Math.max(zoom, 1e-9);
    const height = contentHeight * scaleY / Math.max(zoom, 1e-9);
    const { maxEditorWidth, maxEditorHeight } = getParagraphEditorMetrics();
    const minWorldWidth = Math.min(PARAGRAPH_MIN_WIDTH, maxEditorWidth) / Math.max(zoom, 1e-9);
    const minWorldHeight = Math.min(PARAGRAPH_MIN_HEIGHT, maxEditorHeight) / Math.max(zoom, 1e-9);
    const maxWorldWidth = maxEditorWidth / Math.max(zoom, 1e-9);
    const maxWorldHeight = maxEditorHeight / Math.max(zoom, 1e-9);

    setParagraphDraft((prev) => (prev ? {
      ...prev,
      shape: {
        ...prev.shape,
        width: Math.min(maxWorldWidth, Math.max(minWorldWidth, width)),
        height: Math.min(maxWorldHeight, Math.max(minWorldHeight, height)),
        html,
      },
    } : prev));
  }, [canvasSize.height, canvasSize.width, getParagraphEditorMetrics, zoom]);

  const screenToWorld = useCallback((clientX, clientY) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = rect.width ? canvasSize.width / rect.width : 1;
    const scaleY = rect.height ? canvasSize.height / rect.height : 1;
    const canvasX = (clientX - rect.left) * scaleX;
    const canvasY = (clientY - rect.top) * scaleY;
    const x = (canvasX - pan.x) / zoom;
    const y = (canvasY - pan.y) / zoom;
    return { x, y };
  }, [canvasSize.height, canvasSize.width, pan.x, pan.y, zoom]);

  const getCachedImage = useCallback((src) => {
    if (!src) return null;

    let entry = imageCacheRef.current.get(src);
    if (!entry) {
      const image = new Image();
      image.onload = () => redrawRef.current?.();
      image.src = src;
      entry = image;
      imageCacheRef.current.set(src, entry);
    }

    return entry;
  }, []);

  const inlineFontUrlsInCss = useCallback(async (cssText, baseHref) => {
    const matches = Array.from(cssText.matchAll(CSS_URL_PATTERN));
    if (!matches.length) return cssText;

    let nextCssText = cssText;
    await Promise.all(matches.map(async (match) => {
      const rawUrl = match[2];
      if (!rawUrl || /^(data:|blob:|#)/i.test(rawUrl)) return;

      let absoluteUrl;
      try {
        absoluteUrl = new URL(rawUrl, baseHref || document.baseURI).toString();
      } catch {
        return;
      }

      try {
        let dataUrl = mathFontDataUrlCacheRef.current.get(absoluteUrl);
        if (!dataUrl) {
          const response = await fetch(absoluteUrl);
          if (!response.ok) return;
          dataUrl = await readBlobAsDataUrl(await response.blob());
          mathFontDataUrlCacheRef.current.set(absoluteUrl, dataUrl);
        }
        nextCssText = nextCssText.replace(match[0], `url("${dataUrl}")`);
      } catch {
        // Keep the original URL if a font cannot be fetched; MathLive can still fall back.
      }
    }));

    return nextCssText;
  }, []);

  const getMathCssText = useCallback(async () => {
    if (mathCssTextPromiseRef.current) {
      return mathCssTextPromiseRef.current;
    }

    mathCssTextPromiseRef.current = (async () => {
      const cssRules = [];

      await Promise.all(Array.from(document.styleSheets).map(async (sheet) => {
        let rules;
        try {
          rules = sheet.cssRules;
        } catch {
          rules = null;
        }

        if (!rules) return;

        await Promise.all(Array.from(rules).map(async (rule) => {
          const cssText = rule.cssText || '';
          const isFontFaceRule = rule.type === window.CSSRule?.FONT_FACE_RULE;
          if (
            isFontFaceRule ||
            cssText.includes('.ML__') ||
            cssText.includes('math-field') ||
            cssText.includes(':root')
          ) {
            cssRules.push(isFontFaceRule ? await inlineFontUrlsInCss(cssText, sheet.href) : cssText);
          }
        }));
      }));

      return cssRules.join('\n');
    })();

    return mathCssTextPromiseRef.current;
  }, [inlineFontUrlsInCss]);

  const buildMathAsset = useCallback(async (latex, color, size) => {
    const cacheKey = `${latex}__${color}__${size}`;
    const cached = mathCacheRef.current.get(cacheKey);
    if (cached?.ready) {
      return cached;
    }

    const measureEl = document.createElement('div');
    measureEl.className = 'math-container geometry-math-measure';
    measureEl.contentEditable = 'false';
    measureEl.style.position = 'fixed';
    measureEl.style.left = '-10000px';
    measureEl.style.top = '0';
    measureEl.style.visibility = 'hidden';
    measureEl.style.pointerEvents = 'none';
    measureEl.style.display = 'inline-block';
    measureEl.style.verticalAlign = 'baseline';
    measureEl.style.lineHeight = 'normal';
    measureEl.style.whiteSpace = 'nowrap';
    measureEl.style.color = color;
    measureEl.style.fontSize = `${size}px`;
    measureEl.innerHTML = `\\(${latex}\\)`;
    document.body.appendChild(measureEl);

    try {
      const { renderMathInElement } = await import('mathlive');
      renderMathInElement(measureEl, { renderAccessibleContent: false });
    } catch {
      measureEl.innerHTML = convertLatexToMarkup(latex, { defaultMode: 'inline-math' });
    }

    await document.fonts?.ready;

    const rect = measureEl.getBoundingClientRect();
    const width = Math.max(12, Math.ceil(rect.width) + 6);
    const height = Math.max(12, Math.ceil(rect.height) + 6);
    const markup = measureEl.innerHTML;
    document.body.removeChild(measureEl);

    const cssText = await getMathCssText();
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
        <foreignObject width="100%" height="100%">
          <style>${cssText}</style>
          <div xmlns="http://www.w3.org/1999/xhtml" style="display:inline-block;padding:3px;color:${color};font-size:${size}px;line-height:normal;white-space:nowrap;">
            ${markup}
          </div>
        </foreignObject>
      </svg>
    `;

    const src = encodeSvg(svg);
    const image = new Image();
    const entry = { ready: false, src, image, width, height };
    image.onload = () => {
      entry.ready = true;
      redrawRef.current?.();
    };
    image.onerror = () => {
      entry.ready = true;
    };
    image.src = src;
    mathCacheRef.current.set(cacheKey, entry);
    return entry;
  }, [getMathCssText]);

  const buildParagraphAsset = useCallback((shape) => new Promise(async (resolve) => {
    if (!shape || shape.type !== 'paragraph') {
      resolve(null);
      return;
    }

    const cacheKey = JSON.stringify({
      html: shape.html || '',
      color: shape.color || '#000000',
      size: shape.size || currentTextSize,
      width: shape.width || 0,
      height: shape.height || 0,
      textAlign: shape.textAlign || 'left',
    });
    const cached = paragraphAssetCacheRef.current.get(cacheKey);
    if (cached?.ready) {
      resolve(cached);
      return;
    }

    const cssText = await getMathCssText();
    const width = Math.max(12, Math.ceil(shape.width || 0));
    const height = Math.max(12, Math.ceil(shape.height || 0));
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
        <foreignObject width="100%" height="100%">
          <style>${cssText}</style>
          <div
            xmlns="http://www.w3.org/1999/xhtml"
            style="
              width:${width}px;
              height:${height}px;
              box-sizing:border-box;
              overflow:hidden;
              padding:8px 12px;
              color:${shape.color || '#000000'};
              font-size:${shape.size || currentTextSize}px;
              line-height:1.5;
              text-align:${shape.textAlign || 'left'};
              font-family:Arial,sans-serif;
              background:transparent;
              white-space:normal;
              overflow-wrap:anywhere;
              word-break:break-word;
            "
          >
            ${shape.html || '<p><br /></p>'}
          </div>
        </foreignObject>
      </svg>
    `;

    const src = encodeSvg(svg);
    const image = new Image();
    const entry = { ready: false, src, image, width, height };
    image.onload = () => {
      entry.ready = true;
      resolve(entry);
    };
    image.onerror = () => {
      entry.ready = true;
      resolve(entry);
    };
    image.src = src;
    paragraphAssetCacheRef.current.set(cacheKey, entry);
  }), [currentTextSize, getMathCssText]);

  const waitForImage = useCallback((image) => new Promise((resolve) => {
    if (!image) {
      resolve(null);
      return;
    }
    if (image.complete && image.naturalWidth > 0) {
      resolve(image);
      return;
    }
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
  }), []);

  const findShapeIndexAtPoint = useCallback((point) => {
    const tolerance = 8 / zoom;
    for (let i = shapes.length - 1; i >= 0; i -= 1) {
      const shape = shapes[i];
      if (shape.type === 'line') {
        if (distToSegment(point, shape.start, shape.end) <= tolerance) return i;
      } else if (shape.type === 'circle') {
        const geometry = getCircleGeometry(shape);
        if (!geometry || geometry.radius <= 0) continue;
        const r = geometry.radius;
        const d = distanceBetween(point, geometry.center);
        if (Math.abs(d - r) <= tolerance) return i;
      } else if (shape.type === 'semicircle') {
        const geometry = getSemicircleGeometry(shape);
        if (!geometry || geometry.radius <= 0) continue;
        const angle = Math.atan2(point.y - geometry.center.y, point.x - geometry.center.x);
        if (!angleIsWithinSweep(angle, geometry.startAngle, geometry.endAngle, geometry.anticlockwise)) continue;
        const d = distanceBetween(point, geometry.center);
        if (Math.abs(d - geometry.radius) <= tolerance) return i;
      } else if (shape.type === 'angle') {
        const geometry = getAngleGeometry(shape);
        if (!geometry || geometry.radius <= 0) continue;
        const angle = Math.atan2(point.y - geometry.center.y, point.x - geometry.center.x);
        const d = distanceBetween(point, geometry.center);
        if (
          d <= geometry.radius + tolerance &&
          angleIsWithinSweep(angle, geometry.startAngle, geometry.endAngle, geometry.anticlockwise)
        ) return i;
      } else if (shape.type === 'point') {
        if (Math.hypot(point.x - shape.pos.x, point.y - shape.pos.y) <= tolerance) return i;
      } else if (shape.type === 'text') {
        const ctx = canvasRef.current.getContext('2d');
        const bounds = getShapeBounds(shape, ctx);
        if (pointInRect(point, bounds)) return i;
      } else if (shape.type === 'math') {
        if (pointInRect(point, getMarkupBounds(shape))) return i;
      } else if (shape.type === 'paragraph') {
        if (pointInRect(point, getMarkupBounds(shape))) return i;
      } else if (shape.type === 'image') {
        if (pointInRect(point, getImageBounds(shape))) return i;
      } else if (shape.type === 'pen') {
        for (let p = 1; p < shape.path.length; p += 1) {
          if (distToSegment(point, shape.path[p - 1], shape.path[p]) <= tolerance) return i;
        }
      }
    }
    return -1;
  }, [shapes, zoom]);

  const findLineEndpointHandle = useCallback((point) => {
    const tolerance = 10 / zoom;

    for (let i = selectedIndices.length - 1; i >= 0; i -= 1) {
      const shapeIndex = selectedIndices[i];
      const shape = shapes[shapeIndex];
      if (!shape || shape.type !== 'line') continue;

      const dStart = Math.hypot(point.x - shape.start.x, point.y - shape.start.y);
      if (dStart <= tolerance) return { shapeIndex, endpoint: 'start' };

      const dEnd = Math.hypot(point.x - shape.end.x, point.y - shape.end.y);
      if (dEnd <= tolerance) return { shapeIndex, endpoint: 'end' };
    }

    return null;
  }, [selectedIndices, shapes, zoom]);

  const findImageResizeHandle = useCallback((point) => {
    const tolerance = 10 / zoom;

    for (let i = selectedIndices.length - 1; i >= 0; i -= 1) {
      const shapeIndex = selectedIndices[i];
      const shape = shapes[shapeIndex];
      if (!shape || shape.type !== 'image') continue;

      const handles = getImageResizeHandles(shape);
      for (const [handle, handlePoint] of Object.entries(handles)) {
        if (Math.hypot(point.x - handlePoint.x, point.y - handlePoint.y) <= tolerance) {
          return { shapeIndex, handle };
        }
      }
    }

    return null;
  }, [selectedIndices, shapes, zoom]);

  const findParagraphResizeHandle = useCallback((point) => {
    const tolerance = 10 / zoom;

    for (let i = selectedIndices.length - 1; i >= 0; i -= 1) {
      const shapeIndex = selectedIndices[i];
      const shape = shapes[shapeIndex];
      if (!shape || shape.type !== 'paragraph') continue;

      const handles = getParagraphResizeHandles(shape);
      for (const [handle, handlePoint] of Object.entries(handles)) {
        if (Math.hypot(point.x - handlePoint.x, point.y - handlePoint.y) <= tolerance) {
          return { shapeIndex, handle };
        }
      }
    }

    return null;
  }, [selectedIndices, shapes, zoom]);

  const getSelectionBounds = useCallback((indices = selectedIndices, sourceShapes = shapes) => {
    const ctx = canvasRef.current?.getContext?.('2d');
    return getSelectionBoundsFromShapes(sourceShapes, indices, ctx);
  }, [selectedIndices, shapes]);

  const findSelectionResizeHandle = useCallback((point) => {
    const bounds = getSelectionBounds();
    if (!bounds) return null;
    const tolerance = 10 / zoom;
    const handles = getSelectionHandles(bounds);
    for (const [handle, handlePoint] of Object.entries(handles)) {
      if (Math.hypot(point.x - handlePoint.x, point.y - handlePoint.y) <= tolerance) {
        return { handle, bounds };
      }
    }
    return null;
  }, [getSelectionBounds, zoom]);

  const transformSelectionShapes = useCallback((sourceShapes, indices, fromBounds, toBounds) => {
    const selectedSet = new Set(indices);
    const scale = fromBounds.width > 0 ? toBounds.width / fromBounds.width : 1;

    return sourceShapes.map((shape, index) => {
      if (!selectedSet.has(index)) return shape;
      const nextShape = cloneShapes([shape])[0];

      if (nextShape.start) nextShape.start = scalePointWithinBounds(nextShape.start, fromBounds, toBounds);
      if (nextShape.end) nextShape.end = scalePointWithinBounds(nextShape.end, fromBounds, toBounds);
      if (nextShape.vertex) nextShape.vertex = scalePointWithinBounds(nextShape.vertex, fromBounds, toBounds);
      if (nextShape.pos) nextShape.pos = scalePointWithinBounds(nextShape.pos, fromBounds, toBounds);
      if (nextShape.path) nextShape.path = nextShape.path.map((point) => scalePointWithinBounds(point, fromBounds, toBounds));

      if (nextShape.type === 'circle' && nextShape.center && Number.isFinite(nextShape.radius)) {
        nextShape.center = scalePointWithinBounds(nextShape.center, fromBounds, toBounds);
        nextShape.radius *= scale;
        if (Array.isArray(nextShape.sourcePoints)) {
          nextShape.sourcePoints = nextShape.sourcePoints.map((point) => scalePointWithinBounds(point, fromBounds, toBounds));
        }
      }

      if (nextShape.type === 'angle') {
        if (Number.isFinite(nextShape.radius)) nextShape.radius = Math.max(8, nextShape.radius * scale);
        if (Array.isArray(nextShape.sourcePoints)) {
          nextShape.sourcePoints = nextShape.sourcePoints.map((point) => scalePointWithinBounds(point, fromBounds, toBounds));
        }
      }

      if (nextShape.type === 'text') {
        nextShape.size = Math.max(8, Math.round(nextShape.size * scale));
      }

      if (nextShape.type === 'math' || nextShape.type === 'image') {
        const topLeft = scalePointWithinBounds({ x: nextShape.x, y: nextShape.y }, fromBounds, toBounds);
        nextShape.x = topLeft.x;
        nextShape.y = topLeft.y;
        nextShape.width = Math.max(IMAGE_MIN_SIZE, nextShape.width * scale);
        nextShape.height = Math.max(IMAGE_MIN_SIZE, nextShape.height * scale);
      }

      if (nextShape.type === 'paragraph') {
        const topLeft = scalePointWithinBounds({ x: nextShape.x, y: nextShape.y }, fromBounds, toBounds);
        nextShape.x = topLeft.x;
        nextShape.y = topLeft.y;
        nextShape.width = Math.max(PARAGRAPH_MIN_WIDTH / Math.max(scale, 1), nextShape.width * scale);
        nextShape.height = Math.max(PARAGRAPH_MIN_HEIGHT / Math.max(scale, 1), nextShape.height * scale);
        nextShape.size = Math.max(12, Math.round((nextShape.size || currentTextSize) * scale));
      }

      return nextShape;
    });
  }, [currentTextSize]);

  const renderCanvas = useCallback((ctx, canvas, options = {}) => {
    const {
      includeSelectionUi = true,
      includeDraftShape = true,
      overrides = null,
    } = options;
    const viewState = overrides || {};
    const renderZoom = Number.isFinite(viewState.zoom) ? viewState.zoom : zoom;
    const renderPan = viewState.pan && Number.isFinite(viewState.pan.x) && Number.isFinite(viewState.pan.y) ? viewState.pan : pan;
    const renderShowGrid = typeof viewState.showGrid === 'boolean' ? viewState.showGrid : showGrid;
    const renderShowAxes = typeof viewState.showAxes === 'boolean' ? viewState.showAxes : showAxes;
    const renderCanvasBackgroundColor = viewState.canvasBackgroundColor || canvasBackgroundColor;
    const renderShapes = Array.isArray(viewState.shapes) ? viewState.shapes : shapes;
    const renderSelectedIndices = Array.isArray(viewState.selectedIndices) ? viewState.selectedIndices : selectedIndices;
    const renderSelectionRect = viewState.selectionRect ?? selectionRect;
    const renderDraftShape = Object.prototype.hasOwnProperty.call(viewState, 'draftShape') ? viewState.draftShape : draftShape;
    const renderCurrentTool = viewState.currentTool || currentTool;
    const renderCurrentColor = viewState.currentColor || currentColor;
    const renderHoverWorldPoint = viewState.hoverWorldPoint || hoverWorldPoint;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = renderCanvasBackgroundColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.setTransform(renderZoom, 0, 0, renderZoom, renderPan.x, renderPan.y);

    if (renderShowGrid) {
      const startX = -renderPan.x / renderZoom;
      const startY = -renderPan.y / renderZoom;
      const endX = (canvas.width - renderPan.x) / renderZoom;
      const endY = (canvas.height - renderPan.y) / renderZoom;
      const major = GRID_SIZE * 5;

      ctx.beginPath();
      ctx.strokeStyle = '#e6e6e6';
      ctx.lineWidth = 1 / renderZoom;
      for (let x = Math.floor(startX / GRID_SIZE) * GRID_SIZE; x <= endX; x += GRID_SIZE) {
        ctx.moveTo(x, startY);
        ctx.lineTo(x, endY);
      }
      for (let y = Math.floor(startY / GRID_SIZE) * GRID_SIZE; y <= endY; y += GRID_SIZE) {
        ctx.moveTo(startX, y);
        ctx.lineTo(endX, y);
      }
      ctx.stroke();

      ctx.beginPath();
      ctx.strokeStyle = '#cfcfcf';
      ctx.lineWidth = 1 / renderZoom;
      for (let x = Math.floor(startX / major) * major; x <= endX; x += major) {
        ctx.moveTo(x, startY);
        ctx.lineTo(x, endY);
      }
      for (let y = Math.floor(startY / major) * major; y <= endY; y += major) {
        ctx.moveTo(startX, y);
        ctx.lineTo(endX, y);
      }
      ctx.stroke();
    }

    if (renderShowAxes) {
      ctx.beginPath();
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 1 / renderZoom;
      ctx.moveTo(-renderPan.x / renderZoom, 0);
      ctx.lineTo((canvas.width - renderPan.x) / renderZoom, 0);
      ctx.moveTo(0, -renderPan.y / renderZoom);
      ctx.lineTo(0, (canvas.height - renderPan.y) / renderZoom);
      ctx.stroke();
    }

    const allShapes = includeDraftShape && renderDraftShape ? [...renderShapes, renderDraftShape] : renderShapes;
    allShapes.forEach((shape, index) => {
      const selected = includeSelectionUi && renderSelectedIndices.includes(index) && index < renderShapes.length;
      const isDraft = index >= renderShapes.length;
      const strokeWidth = shape.strokeWidth || 1.5;
      ctx.save();
      ctx.strokeStyle = shape.color || '#000000';
      ctx.fillStyle = shape.color || '#000000';
      ctx.lineWidth = strokeWidth / renderZoom;

      if (selected) {
        ctx.shadowColor = 'rgba(33,150,243,0.45)';
        ctx.shadowBlur = 8;
      }

      if (shape.type === 'line') {
        const lineStyle = LINE_STYLE_OPTIONS.find((option) => option.id === (shape.lineStyle || 'solid'));
        const arrowMode = shape.arrowMode || 'none';
        const segmentMarking = shape.segmentMarking || 'none';
        const parallelMarker = shape.parallelMarker || 'none';
        ctx.setLineDash((lineStyle?.dash || []).map((value) => value / renderZoom));
        ctx.beginPath();
        ctx.moveTo(shape.start.x, shape.start.y);
        ctx.lineTo(shape.end.x, shape.end.y);
        ctx.stroke();
        ctx.setLineDash([]);

        if (arrowMode === 'start' || arrowMode === 'both') {
          drawArrowHead(ctx, shape.end, shape.start, renderZoom);
        }
        if (arrowMode === 'end' || arrowMode === 'both') {
          drawArrowHead(ctx, shape.start, shape.end, renderZoom);
        }
        drawLineMarkings(ctx, shape.start, shape.end, segmentMarking, renderZoom);
        drawParallelLineMarkers(ctx, shape.start, shape.end, parallelMarker, renderZoom);

        if (selected) {
          const handleRadius = 5 / renderZoom;
          ctx.beginPath();
          ctx.fillStyle = '#ffffff';
          ctx.strokeStyle = '#1976d2';
          ctx.lineWidth = 2 / renderZoom;
          ctx.arc(shape.start.x, shape.start.y, handleRadius, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();

          ctx.beginPath();
          ctx.arc(shape.end.x, shape.end.y, handleRadius, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
        }
      }

      if (shape.type === 'circle') {
        const geometry = getCircleGeometry(shape);
        const draftPoints = Array.isArray(shape.sourcePoints) ? shape.sourcePoints : [];
        const previewPoint = shape.previewPoint || null;
        const previewGeometry = !geometry && shape.construction === 'three-points' && draftPoints.length >= 2 && previewPoint
          ? circumcircleFromPoints(draftPoints[0], draftPoints[1], previewPoint)
          : null;
        const finalGeometry = geometry || previewGeometry;

        if (finalGeometry?.radius > 0) {
          ctx.beginPath();
          ctx.arc(finalGeometry.center.x, finalGeometry.center.y, finalGeometry.radius, 0, Math.PI * 2);
          ctx.stroke();
        }

        if (isDraft && shape.construction === 'center-circumference' && shape.center) {
          ctx.beginPath();
          ctx.fillStyle = shape.color || '#000000';
          ctx.arc(shape.center.x, shape.center.y, 3.5 / renderZoom, 0, Math.PI * 2);
          ctx.fill();

          if (previewPoint) {
            ctx.beginPath();
            ctx.arc(previewPoint.x, previewPoint.y, 3 / renderZoom, 0, Math.PI * 2);
            ctx.fill();
          }
        }

        if (isDraft && shape.construction === 'three-points') {
          [...draftPoints, ...(previewPoint ? [previewPoint] : [])].forEach((point) => {
            ctx.beginPath();
            ctx.fillStyle = shape.color || '#000000';
            ctx.arc(point.x, point.y, 3.5 / renderZoom, 0, Math.PI * 2);
            ctx.fill();
          });
        }
      }

      if (shape.type === 'semicircle') {
        const geometry = getSemicircleGeometry(shape);
        if (geometry?.radius > 0) {
          ctx.beginPath();
          ctx.arc(
            geometry.center.x,
            geometry.center.y,
            geometry.radius,
            geometry.startAngle,
            geometry.endAngle,
            geometry.anticlockwise
          );
          ctx.stroke();
        }
      }

      if (shape.type === 'angle') {
        drawAngleIndicator(ctx, shape, renderZoom);

        if (isDraft && Array.isArray(shape.sourcePoints)) {
          [...shape.sourcePoints, ...(shape.previewPoint ? [shape.previewPoint] : [])].forEach((point) => {
            ctx.beginPath();
            ctx.fillStyle = shape.color || '#000000';
            ctx.arc(point.x, point.y, 3.5 / renderZoom, 0, Math.PI * 2);
            ctx.fill();
          });
        }
      }

      if (shape.type === 'point') {
        ctx.beginPath();
        ctx.arc(shape.pos.x, shape.pos.y, 3.5 / renderZoom, 0, Math.PI * 2);
        ctx.fill();
      }

      if (shape.type === 'text') {
        ctx.font = `${shape.size}px Arial`;
        ctx.fillText(shape.text, shape.pos.x, shape.pos.y);
      }

      if (shape.type === 'math') {
        const cached = mathCacheRef.current.get(`${shape.latex}__${shape.color}__${shape.size}`);
        if (cached?.image?.complete && cached.image.naturalWidth > 0) {
          ctx.drawImage(cached.image, shape.x, shape.y, shape.width, shape.height);
        } else {
          ctx.fillStyle = '#f8fafc';
          ctx.fillRect(shape.x, shape.y, shape.width, shape.height);
          ctx.strokeStyle = '#cbd5e1';
          ctx.strokeRect(shape.x, shape.y, shape.width, shape.height);
        }
      }

      if (shape.type === 'image') {
        const image = getCachedImage(shape.src);
        if (image?.complete && image.naturalWidth > 0) {
          ctx.drawImage(image, shape.x, shape.y, shape.width, shape.height);
        } else {
          ctx.fillStyle = '#f3f4f6';
          ctx.fillRect(shape.x, shape.y, shape.width, shape.height);
          ctx.strokeStyle = '#cbd5e1';
          ctx.strokeRect(shape.x, shape.y, shape.width, shape.height);
        }

        if (selected) {
          const bounds = getImageBounds(shape);
          const handles = Object.values(getImageResizeHandles(shape));
          const handleRadius = 5 / renderZoom;

          ctx.strokeStyle = '#1976d2';
          ctx.lineWidth = 1.5 / renderZoom;
          ctx.setLineDash([5 / renderZoom, 3 / renderZoom]);
          ctx.strokeRect(bounds.x, bounds.y, bounds.width, bounds.height);
          ctx.setLineDash([]);

          handles.forEach((handle) => {
            ctx.beginPath();
            ctx.fillStyle = '#ffffff';
            ctx.arc(handle.x, handle.y, handleRadius, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
          });
        }
      }

      if (shape.type === 'paragraph' && selected) {
        const bounds = getMarkupBounds(shape);
        const handles = Object.values(getParagraphResizeHandles(shape));
        const handleRadius = 5 / renderZoom;

        ctx.strokeStyle = '#1976d2';
        ctx.lineWidth = 1.5 / renderZoom;
        ctx.setLineDash([5 / renderZoom, 3 / renderZoom]);
        ctx.strokeRect(bounds.x, bounds.y, bounds.width, bounds.height);
        ctx.setLineDash([]);

        handles.forEach((handle) => {
          ctx.beginPath();
          ctx.fillStyle = '#ffffff';
          ctx.arc(handle.x, handle.y, handleRadius, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
        });
      }

      if (shape.type === 'pen' && shape.path.length > 1) {
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.beginPath();
        ctx.moveTo(shape.path[0].x, shape.path[0].y);
        for (let i = 1; i < shape.path.length; i += 1) ctx.lineTo(shape.path[i].x, shape.path[i].y);
        ctx.stroke();
      }

      ctx.restore();
    });

    if (includeSelectionUi && renderSelectionRect && renderCurrentTool === 'select') {
      const rect = normalizeRect(renderSelectionRect);
      ctx.save();
      ctx.fillStyle = 'rgba(0,149,255,0.1)';
      ctx.strokeStyle = 'rgba(0,149,255,0.8)';
      ctx.lineWidth = 1 / renderZoom;
      ctx.setLineDash([6 / renderZoom, 4 / renderZoom]);
      ctx.fillRect(rect.x, rect.y, rect.width, rect.height);
      ctx.strokeRect(rect.x, rect.y, rect.width, rect.height);
      ctx.restore();
    }

    if (includeSelectionUi && renderCurrentTool === 'select' && renderSelectedIndices.length) {
      const groupBounds = getSelectionBoundsFromShapes(renderShapes, renderSelectedIndices, ctx);
      if (groupBounds) {
        const handles = Object.values(getSelectionHandles(groupBounds));
        ctx.save();
        ctx.strokeStyle = 'rgba(0,149,255,0.95)';
        ctx.lineWidth = 1.5 / renderZoom;
        ctx.setLineDash([5 / renderZoom, 3 / renderZoom]);
        ctx.strokeRect(groupBounds.x, groupBounds.y, groupBounds.width, groupBounds.height);
        ctx.setLineDash([]);
        handles.forEach((handle) => {
          ctx.beginPath();
          ctx.fillStyle = '#ffffff';
          ctx.strokeStyle = '#1976d2';
          ctx.rect(
            handle.x - SELECTION_HANDLE_SIZE / renderZoom,
            handle.y - SELECTION_HANDLE_SIZE / renderZoom,
            (SELECTION_HANDLE_SIZE * 2) / renderZoom,
            (SELECTION_HANDLE_SIZE * 2) / renderZoom
          );
          ctx.fill();
          ctx.stroke();
        });
        ctx.restore();
      }
    }

    if (
      includeSelectionUi &&
      renderCurrentTool === 'circle' &&
      circleShape === 'circle' &&
      circleMode === 'three-points' &&
      !renderDraftShape &&
      renderHoverWorldPoint
    ) {
      ctx.save();
      ctx.setTransform(renderZoom, 0, 0, renderZoom, renderPan.x, renderPan.y);
      ctx.beginPath();
      ctx.fillStyle = renderCurrentColor;
      ctx.arc(renderHoverWorldPoint.x, renderHoverWorldPoint.y, 3.5 / renderZoom, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

  }, [canvasBackgroundColor, circleMode, circleShape, currentColor, currentTool, draftShape, getCachedImage, hoverWorldPoint, pan.x, pan.y, selectedIndices, selectionRect, shapes, showAxes, showGrid, zoom]);

  const renderParagraphShapesToCanvas = useCallback(async (ctx, paragraphShapes = []) => {
    paragraphShapes.forEach((shape) => {
      const text = getParagraphPlainText(shape?.html || '');
      if (!text) return;

      const paddingX = 12;
      const paddingY = 8;
      const fontSize = shape.size || currentTextSize;
      const lineHeight = fontSize * 1.5;
      const maxWidth = Math.max(1, shape.width - (paddingX * 2));
      const maxLines = Math.max(1, Math.floor(Math.max(shape.height - (paddingY * 2), lineHeight) / lineHeight));
      const align = shape.textAlign === 'center' || shape.textAlign === 'right' ? shape.textAlign : 'left';
      const paragraphs = text.split('\n');
      const lines = [];

      ctx.save();
      ctx.font = `${fontSize}px Arial`;
      ctx.textBaseline = 'top';
      ctx.fillStyle = shape.color || '#000000';

      paragraphs.forEach((paragraph, paragraphIndex) => {
        const words = paragraph.trim() ? paragraph.trim().split(/\s+/) : [''];
        let currentLine = '';

        words.forEach((word) => {
          const candidate = currentLine ? `${currentLine} ${word}` : word;
          if (ctx.measureText(candidate).width <= maxWidth || !currentLine) {
            currentLine = candidate;
            return;
          }
          lines.push(currentLine);
          currentLine = word;
        });

        if (currentLine || !paragraph.trim()) {
          lines.push(currentLine);
        }

        if (paragraphIndex < paragraphs.length - 1 && lines[lines.length - 1] !== '') {
          lines.push('');
        }
      });

      lines.slice(0, maxLines).forEach((line, index) => {
        const y = shape.y + paddingY + (index * lineHeight);
        let x = shape.x + paddingX;

        if (align === 'center') {
          x = shape.x + (shape.width / 2);
        } else if (align === 'right') {
          x = shape.x + shape.width - paddingX;
        }

        ctx.textAlign = align;
        if (line) {
          ctx.fillText(line, x, y, maxWidth);
        }
        if (y + lineHeight > shape.y + shape.height) return;
      });

      ctx.restore();
    });
  }, [currentTextSize]);

  const renderSlideToDataUrl = useCallback(async (slideState, { mimeType = 'image/jpeg', quality = 0.92 } = {}) => {
    const normalizedState = normalizeDiagramCanvasState(slideState);

    await Promise.all(normalizedState.shapes.map(async (shape) => {
      if (shape?.type === 'math') {
        await buildMathAsset(shape.latex || '', shape.color || '#000000', shape.size || currentTextSize);
      }
      if (shape?.type === 'image') {
        await waitForImage(getCachedImage(shape.src));
      }
      if (shape?.type === 'paragraph') {
        await buildParagraphAsset(shape);
      }
    }));

    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = canvasSize.width;
    exportCanvas.height = canvasSize.height;
    const exportCtx = exportCanvas.getContext('2d');
    renderCanvas(exportCtx, exportCanvas, {
      includeSelectionUi: false,
      includeDraftShape: false,
      overrides: {
        ...normalizedState,
        currentTool: 'pointer',
        selectedIndices: [],
        selectionRect: null,
        draftShape: null,
        hoverWorldPoint: null,
      },
    });
    await renderParagraphShapesToCanvas(
      exportCtx,
      normalizedState.shapes.filter((shape) => shape?.type === 'paragraph')
    );
    return exportCanvas.toDataURL(mimeType, quality);
  }, [
    buildMathAsset,
    buildParagraphAsset,
    canvasSize.height,
    canvasSize.width,
    currentTextSize,
    getCachedImage,
    renderCanvas,
    renderParagraphShapesToCanvas,
    waitForImage,
  ]);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    renderCanvas(ctx, canvas, { includeSelectionUi: true, includeDraftShape: true });
  }, [renderCanvas]);

  useEffect(() => {
    redrawRef.current = redraw;
    redraw();
  }, [redraw]);

  const beginTextInput = useCallback((worldPoint, clientX, clientY) => {
    const rect = containerRef.current.getBoundingClientRect();
    setTextDraft({
      text: '',
      worldPoint,
      left: clientX - rect.left,
      top: clientY - rect.top,
    });
    requestAnimationFrame(() => textInputRef.current?.focus());
  }, []);

  const beginParagraphInput = useCallback((worldPoint, existingIndex = null) => {
    paragraphOutsideCloseReadyRef.current = false;
    const existingShape = existingIndex !== null ? shapes[existingIndex] : null;
    const screenX = (worldPoint.x * zoom) + pan.x;
    const screenY = (worldPoint.y * zoom) + pan.y;
    const { viewportWidth: maxScreenWidth, maxEditorHeight } = getParagraphEditorMetrics();
    const { height: maxScreenHeight } = getParagraphViewportMetrics();
    const desiredScreenWidth = Math.min(PARAGRAPH_DEFAULT_WIDTH, maxScreenWidth);
    const desiredScreenHeight = Math.min(PARAGRAPH_DEFAULT_HEIGHT, maxEditorHeight);
    const maxLeft = Math.max(PARAGRAPH_VIEWPORT_PADDING, maxScreenWidth - desiredScreenWidth + PARAGRAPH_VIEWPORT_PADDING);
    const maxTop = Math.max(PARAGRAPH_VIEWPORT_PADDING, maxScreenHeight - desiredScreenHeight + PARAGRAPH_VIEWPORT_PADDING);
    const clampedScreenLeft = Math.min(Math.max(PARAGRAPH_VIEWPORT_PADDING, screenX), maxLeft);
    const clampedScreenTop = Math.min(Math.max(PARAGRAPH_VIEWPORT_PADDING, screenY), maxTop);
    const clampedWorldX = (clampedScreenLeft - pan.x) / zoom;
    const clampedWorldY = (clampedScreenTop - pan.y) / zoom;
    const nextShape = existingShape?.type === 'paragraph'
      ? cloneShapes([existingShape])[0]
      : {
          type: 'paragraph',
          x: clampedWorldX,
          y: clampedWorldY,
          width: desiredScreenWidth / zoom,
          height: desiredScreenHeight / zoom,
          html: '<p><br></p>',
          color: currentColor,
          size: currentTextSize,
          textAlign: 'left',
        };

    setParagraphDraft({
      shapeIndex: existingIndex,
      originalShape: existingShape ? cloneShapes([existingShape])[0] : null,
      shape: {
        ...nextShape,
        color: nextShape.color || currentColor,
        size: nextShape.size || currentTextSize,
      },
    });
    setParagraphMathDraft(null);
    setSelectedIndices(existingIndex !== null ? [existingIndex] : []);

    requestAnimationFrame(() => {
      const editor = paragraphEditorRef.current;
      if (!editor) return;
      editor.focus();
      const selection = window.getSelection?.();
      if (!selection) return;
      const range = document.createRange();
      range.selectNodeContents(editor);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
      paragraphSelectionRef.current = range.cloneRange();
      requestAnimationFrame(() => {
        paragraphOutsideCloseReadyRef.current = true;
      });
    });
  }, [currentColor, currentTextSize, getParagraphEditorMetrics, getParagraphViewportMetrics, pan.x, pan.y, shapes, zoom]);

  const finalizeText = useCallback(() => {
    if (!textDraft || !textDraft.text.trim()) {
      setTextDraft(null);
      return;
    }
    const next = [
      ...shapes,
      {
        type: 'text',
        pos: textDraft.worldPoint,
        text: textDraft.text.trim(),
        color: currentColor,
        size: currentTextSize,
      },
    ];
    commitShapes(next);
    setTextDraft(null);
  }, [commitShapes, currentColor, currentTextSize, shapes, textDraft]);

  const finalizeMath = useCallback(async (latex) => {
    if (!mathDraft) {
      return;
    }

    const trimmed = latex?.trim();
    if (!trimmed) {
      setMathDraft(null);
      return;
    }

    const mathColor = mathDraft.color || currentColor;
    const mathSize = mathDraft.size || currentTextSize;
    const asset = await buildMathAsset(trimmed, mathColor, mathSize);
    const nextShape = {
      type: 'math',
      latex: trimmed,
      x: mathDraft.worldPoint.x,
      y: mathDraft.worldPoint.y - (asset.height / 2),
      width: asset.width,
      height: asset.height,
      color: mathColor,
      size: mathSize,
      src: asset.src,
    };

    const next = Number.isInteger(mathDraft.shapeIndex)
      ? shapes.map((shape, index) => (index === mathDraft.shapeIndex ? nextShape : shape))
      : [...shapes, nextShape];

    commitShapes(next);
    if (Number.isInteger(mathDraft.shapeIndex)) {
      setSelectedIndices([mathDraft.shapeIndex]);
    }
    setMathDraft(null);
  }, [buildMathAsset, commitShapes, currentColor, currentTextSize, mathDraft, shapes]);

  const handleCanvasDoubleClick = useCallback((event) => {
    if (readOnly || currentTool !== 'pointer' || paragraphDraft) return;

    const world = screenToWorld(event.clientX, event.clientY);
    const shapeIndex = findShapeIndexAtPoint(world);
    const targetShape = shapeIndex >= 0 ? shapes[shapeIndex] : null;

    if (targetShape?.type !== 'math') return;

    interactionRef.current.mode = null;
    interactionRef.current.start = null;
    interactionRef.current.initialShapes = null;
    setSelectedIndices([shapeIndex]);
    setMathDraft({
      worldPoint: {
        x: targetShape.x,
        y: targetShape.y + (targetShape.height / 2),
      },
      anchorRect: {
        left: event.clientX,
        top: event.clientY,
      },
      initialLatex: targetShape.latex || '',
      shapeIndex,
      color: targetShape.color || currentColor,
      size: targetShape.size || currentTextSize,
    });
  }, [currentColor, currentTextSize, currentTool, findShapeIndexAtPoint, paragraphDraft, readOnly, screenToWorld, shapes]);

  const finalizeParagraph = useCallback(() => {
    setParagraphMathDraft(null);
    const editor = paragraphEditorRef.current;
    const nextDraft = paragraphDraft
      ? {
          ...paragraphDraft,
          shape: {
            ...paragraphDraft.shape,
            html: editor?.innerHTML ?? paragraphDraft.shape.html,
          },
        }
      : null;

    if (!nextDraft) return;

    if (isParagraphContentEmpty(nextDraft.shape.html)) {
      setParagraphDraft(null);
      return;
    }

    if (nextDraft.shapeIndex !== null) {
      const nextShapes = shapes.map((shape, index) => (
        index === nextDraft.shapeIndex ? nextDraft.shape : shape
      ));
      commitShapes(nextShapes);
      setSelectedIndices([nextDraft.shapeIndex]);
    } else {
      commitShapes([...shapes, nextDraft.shape]);
      setSelectedIndices([shapes.length]);
    }

    setParagraphDraft(null);
  }, [commitShapes, paragraphDraft, shapes]);

  const cancelParagraph = useCallback(() => {
    paragraphOutsideCloseReadyRef.current = false;
    setParagraphMathDraft(null);
    setParagraphDraft(null);
  }, []);

  const applyParagraphCommand = useCallback((command) => {
    const editor = paragraphEditorRef.current;
    if (!editor) return;
    editor.focus();
    restoreParagraphSelection();
    document.execCommand(command, false, null);
    saveParagraphSelection();
    syncParagraphDraftFromEditor();
  }, [restoreParagraphSelection, saveParagraphSelection, syncParagraphDraftFromEditor]);

  const applyParagraphAlignment = useCallback((alignment) => {
    const commandMap = {
      left: 'justifyLeft',
      center: 'justifyCenter',
      right: 'justifyRight',
      justify: 'justifyFull',
    };
    const command = commandMap[alignment];
    if (!command) return;
    applyParagraphCommand(command);
    setParagraphDraft((prev) => (prev ? {
      ...prev,
      shape: {
        ...prev.shape,
        textAlign: alignment,
      },
    } : prev));
  }, [applyParagraphCommand]);

  const openParagraphMathModal = useCallback((anchorRect, options = {}) => {
    const {
      initialLatex = '',
      targetNode = null,
    } = options;
    setParagraphMathDraft({
      anchorRect,
      initialLatex,
      targetNode,
    });
  }, []);

  const handleParagraphMathButtonClick = useCallback((event) => {
    saveParagraphSelection();
    const rect = event.currentTarget.getBoundingClientRect();
    openParagraphMathModal({
      left: rect.left,
      top: rect.bottom,
    });
  }, [openParagraphMathModal, saveParagraphSelection]);

  const handleParagraphMathDoubleClick = useCallback((event) => {
    const editor = paragraphEditorRef.current;
    const targetNode = event.target instanceof Element
      ? event.target.closest('.math-container[data-math-content]')
      : null;
    if (!editor || !targetNode || !editor.contains(targetNode)) return;

    event.preventDefault();
    event.stopPropagation();

    const selection = window.getSelection?.();
    if (selection) {
      const range = document.createRange();
      range.setStartAfter(targetNode);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      paragraphSelectionRef.current = range.cloneRange();
    }

    const rect = targetNode.getBoundingClientRect();
    openParagraphMathModal(
      {
        left: rect.left,
        top: rect.bottom,
      },
      {
        initialLatex: targetNode.getAttribute('data-math-content') || '',
        targetNode,
      }
    );
  }, [openParagraphMathModal]);

  const insertParagraphMath = useCallback(async (latex) => {
    const trimmed = latex?.trim();
    const editor = paragraphEditorRef.current;
    const existingNode = paragraphMathDraft?.targetNode;
    setParagraphMathDraft(null);
    if (!editor || !trimmed) return;

    editor.focus();
    if (!existingNode || !editor.contains(existingNode)) {
      restoreParagraphSelection();
    }
    const selection = window.getSelection?.();
    if (!selection || !selection.rangeCount) return;

    const nextRange = document.createRange();
    let mathSpan = existingNode instanceof Element && editor.contains(existingNode) ? existingNode : null;

    if (mathSpan) {
      mathSpan.setAttribute('data-math-content', trimmed);
      mathSpan.innerHTML = `\\(${trimmed}\\)`;
      nextRange.setStartAfter(mathSpan);
    } else {
      const range = selection.getRangeAt(0);
      range.deleteContents();

      mathSpan = document.createElement('span');
      mathSpan.className = 'math-container';
      mathSpan.contentEditable = 'false';
      mathSpan.style.cursor = 'pointer';
      mathSpan.setAttribute('data-math-content', trimmed);
      mathSpan.innerHTML = `\\(${trimmed}\\)`;
      range.insertNode(mathSpan);

      const spacer = document.createTextNode(' ');
      mathSpan.parentNode?.insertBefore(spacer, mathSpan.nextSibling);
      nextRange.setStartAfter(spacer);
    }

    nextRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(nextRange);
    paragraphSelectionRef.current = nextRange.cloneRange();

    await renderParagraphMath(editor);
    syncParagraphDraftFromEditor();
  }, [paragraphMathDraft, renderParagraphMath, restoreParagraphSelection, syncParagraphDraftFromEditor]);

  const addImageFromDataUrl = useCallback((src) => {
    const image = new Image();
    image.onload = () => {
      const viewportWidth = canvasSize.width / zoom;
      const viewportHeight = canvasSize.height / zoom;
      const maxWidth = viewportWidth * 0.5;
      const maxHeight = viewportHeight * 0.5;
      const scale = Math.min(maxWidth / image.naturalWidth, maxHeight / image.naturalHeight, 1);
      const width = Math.max(IMAGE_MIN_SIZE, Math.round(image.naturalWidth * scale));
      const height = Math.max(IMAGE_MIN_SIZE, Math.round(image.naturalHeight * scale));
      const centerX = (-pan.x / zoom) + (viewportWidth / 2);
      const centerY = (-pan.y / zoom) + (viewportHeight / 2);

      commitShapes([
        ...shapes,
        {
          type: 'image',
          src,
          x: centerX - (width / 2),
          y: centerY - (height / 2),
          width,
          height,
          naturalWidth: image.naturalWidth,
          naturalHeight: image.naturalHeight,
        },
      ]);
      imageCacheRef.current.set(src, image);
    };
    image.src = src;
  }, [canvasSize.height, canvasSize.width, commitShapes, pan.x, pan.y, shapes, zoom]);

  const finalizeCircle = useCallback((circleDefinition) => {
    if (!circleDefinition?.center || !Number.isFinite(circleDefinition.radius) || circleDefinition.radius <= 0.5) {
      return;
    }

    commitShapes([
      ...shapes,
      {
        type: 'circle',
        center: clonePoint(circleDefinition.center),
        radius: circleDefinition.radius,
        color: currentColor,
        strokeWidth: circleDefinition.strokeWidth || 1.5,
        construction: circleDefinition.construction || circleMode,
        sourcePoints: Array.isArray(circleDefinition.sourcePoints)
          ? circleDefinition.sourcePoints.map((point) => clonePoint(point))
          : [],
      },
      {
        type: 'point',
        pos: clonePoint(circleDefinition.center),
        color: currentColor,
      },
    ]);
  }, [circleMode, commitShapes, currentColor, shapes]);

  const finalizeAngle = useCallback((points) => {
    if (!Array.isArray(points) || points.length < 3) return;
    const [start, vertex, end] = points;
    const startDistance = distanceBetween(vertex, start);
    const endDistance = distanceBetween(vertex, end);
    if (startDistance <= 1 || endDistance <= 1) return;

    commitShapes([
      ...shapes,
      {
        type: 'angle',
        start: clonePoint(start),
        vertex: clonePoint(vertex),
        end: clonePoint(end),
        radius: Math.max(18, Math.min(startDistance, endDistance, 52)),
        shade: 0.18,
        useReflex: false,
        color: currentColor,
        strokeWidth: 1.5,
        sourcePoints: points.slice(0, 3).map((point) => clonePoint(point)),
      },
    ]);
  }, [commitShapes, currentColor, shapes]);

  const handlePointerDown = useCallback((event) => {
    if (!canvasRef.current) return;
    if (readOnly) return;
    containerRef.current?.focus();
    const world = screenToWorld(event.clientX, event.clientY);
    interactionRef.current.start = world;
    interactionRef.current.startPan = pan;
    interactionRef.current.startClient = { x: event.clientX, y: event.clientY };

    if (currentTool === 'text') {
      beginTextInput(world, event.clientX, event.clientY);
      return;
    }

    if (currentTool === 'paragraph') {
      const shapeIndex = findShapeIndexAtPoint(world);
      if (shapeIndex >= 0 && shapes[shapeIndex]?.type === 'paragraph') {
        beginParagraphInput(world, shapeIndex);
      } else {
        beginParagraphInput(world, null);
      }
      return;
    }

    if (currentTool === 'math') {
      setMathDraft({
        worldPoint: world,
        anchorRect: {
          left: event.clientX,
          top: event.clientY,
        },
      });
      return;
    }

    if (currentTool === 'pan') {
      interactionRef.current.mode = 'panning';
      return;
    }

    if (currentTool === 'select') {
      const resizeHandleHit = findSelectionResizeHandle(world);
      if (resizeHandleHit && safeSelectedIndices.length) {
        const oppositeMap = {
          nw: { x: resizeHandleHit.bounds.x + resizeHandleHit.bounds.width, y: resizeHandleHit.bounds.y + resizeHandleHit.bounds.height },
          ne: { x: resizeHandleHit.bounds.x, y: resizeHandleHit.bounds.y + resizeHandleHit.bounds.height },
          sw: { x: resizeHandleHit.bounds.x + resizeHandleHit.bounds.width, y: resizeHandleHit.bounds.y },
          se: { x: resizeHandleHit.bounds.x, y: resizeHandleHit.bounds.y },
        };
        interactionRef.current.mode = 'resizing-selection';
        interactionRef.current.initialShapes = cloneShapes(shapes);
        interactionRef.current.selectionBoundsStart = resizeHandleHit.bounds;
        interactionRef.current.selectionResizeHandle = resizeHandleHit.handle;
        interactionRef.current.selectionResizeAnchor = oppositeMap[resizeHandleHit.handle];
        return;
      }

      const currentSelectionBounds = getSelectionBounds();
      if (currentSelectionBounds && pointInRect(world, currentSelectionBounds) && safeSelectedIndices.length) {
        interactionRef.current.mode = 'moving-selection';
        interactionRef.current.initialShapes = cloneShapes(shapes);
        interactionRef.current.selectedShapeStart = world;
        return;
      }

      interactionRef.current.mode = 'selecting';
      setSelectionRect({ x: world.x, y: world.y, width: 0, height: 0 });
      return;
    }

    if (currentTool === 'pointer') {
      const paragraphHandleHit = findParagraphResizeHandle(world);
      if (paragraphHandleHit) {
        setSelectedIndices([paragraphHandleHit.shapeIndex]);
        interactionRef.current.mode = 'resizing-paragraph-width';
        interactionRef.current.initialShapes = cloneShapes(shapes);
        interactionRef.current.draggedParagraphHandle = paragraphHandleHit;
        return;
      }

      const imageHandleHit = findImageResizeHandle(world);
      if (imageHandleHit) {
        setSelectedIndices([imageHandleHit.shapeIndex]);
        interactionRef.current.mode = 'resizing-image';
        interactionRef.current.initialShapes = cloneShapes(shapes);
        interactionRef.current.draggedImageHandle = imageHandleHit;
        return;
      }

      const handleHit = findLineEndpointHandle(world);
      if (handleHit) {
        setSelectedIndices([handleHit.shapeIndex]);
        interactionRef.current.mode = 'moving-line-endpoint';
        interactionRef.current.initialShapes = cloneShapes(shapes);
        interactionRef.current.draggedLineHandle = handleHit;
        return;
      }

      const index = findShapeIndexAtPoint(world);
      if (index >= 0) {
        if (event.shiftKey) {
          const nextSelection = safeSelectedIndices.includes(index)
            ? safeSelectedIndices.filter((selectedIndex) => selectedIndex !== index)
            : [...safeSelectedIndices, index];
          setSelectedIndices(nextSelection);
          interactionRef.current.mode = null;
          interactionRef.current.initialShapes = null;
          interactionRef.current.selectedShapeStart = null;
          return;
        }

        const nextSelection = [index];
        setSelectedIndices(nextSelection);
        interactionRef.current.mode = 'moving';
        interactionRef.current.initialShapes = cloneShapes(shapes);
        interactionRef.current.selectedShapeStart = world;
      } else {
        if (!event.shiftKey) setSelectedIndices([]);
        interactionRef.current.mode = null;
      }
      return;
    }

    if (currentTool === 'point') {
      commitShapes([
        ...shapes,
        { type: 'point', pos: world, color: currentColor },
      ]);
      return;
    }

    if (currentTool === 'circle') {
      if (circleShape === 'semicircle') {
        if (circleMode === 'diameter-drag') {
          interactionRef.current.mode = 'drawing-shape';
          setDraftShape({
            type: 'semicircle',
            start: world,
            end: world,
            color: currentColor,
            strokeWidth: 1.5,
            construction: circleMode,
          });
        }
        return;
      }

      if (circleMode === 'center-radius') {
        interactionRef.current.mode = 'drawing-circle-center';
        setDraftShape({
          type: 'circle',
          center: world,
          radius: 0,
          color: currentColor,
          strokeWidth: 1.5,
          construction: circleMode,
        });
        return;
      }

      if (circleMode === 'center-circumference') {
        const currentDraft = draftShape?.type === 'circle' && draftShape.construction === circleMode ? draftShape : null;
        if (currentDraft?.center) {
          finalizeCircle({
            center: currentDraft.center,
            radius: distanceBetween(currentDraft.center, world),
            construction: circleMode,
            strokeWidth: currentDraft.strokeWidth || 1.5,
            sourcePoints: [currentDraft.center, world],
          });
          setDraftShape(null);
        } else {
          setDraftShape({
            type: 'circle',
            center: world,
            radius: 0,
            color: currentColor,
            strokeWidth: 1.5,
            construction: circleMode,
            previewPoint: world,
          });
        }
        return;
      }

      if (circleMode === 'three-points') {
        const currentPoints = draftShape?.type === 'circle' && draftShape.construction === circleMode
          ? draftShape.sourcePoints || []
          : [];
        const nextPoints = [...currentPoints, world];
        if (nextPoints.length >= 3) {
          const geometry = circumcircleFromPoints(nextPoints[0], nextPoints[1], nextPoints[2]);
          if (geometry) {
            finalizeCircle({
              ...geometry,
              construction: circleMode,
              strokeWidth: draftShape?.strokeWidth || 1.5,
              sourcePoints: nextPoints.slice(0, 3),
            });
          }
          setDraftShape(null);
        } else {
          setDraftShape({
            type: 'circle',
            color: currentColor,
            strokeWidth: 1.5,
            construction: circleMode,
            sourcePoints: nextPoints,
            previewPoint: world,
          });
        }
        return;
      }
    }

    if (currentTool === 'line') {
      interactionRef.current.mode = 'drawing-shape';
      setDraftShape({
        type: 'line',
        start: world,
        end: world,
        color: currentColor,
        strokeWidth: 1.5,
        lineStyle: 'solid',
        arrowMode: 'none',
        segmentMarking: 'none',
        parallelMarker: 'none',
      });
      return;
    }

    if (currentTool === 'angle') {
      const currentPoints = draftShape?.type === 'angle' ? draftShape.sourcePoints || [] : [];
      const nextPoints = [...currentPoints, world];
      if (nextPoints.length >= 3) {
        finalizeAngle(nextPoints.slice(0, 3));
        setDraftShape(null);
      } else {
        setDraftShape({
          type: 'angle',
          color: currentColor,
          strokeWidth: 1.5,
          shade: 0.18,
          useReflex: false,
          sourcePoints: nextPoints,
          previewPoint: world,
        });
      }
      return;
    }

    if (currentTool === 'pen') {
      interactionRef.current.mode = 'drawing-pen';
      setDraftShape({
        type: 'pen',
        path: [world],
        color: currentColor,
      });
    }
  }, [beginParagraphInput, beginTextInput, circleMode, circleShape, commitShapes, currentColor, currentTool, draftShape, finalizeAngle, finalizeCircle, findImageResizeHandle, findLineEndpointHandle, findParagraphResizeHandle, findSelectionResizeHandle, findShapeIndexAtPoint, getSelectionBounds, pan, readOnly, safeSelectedIndices, safeSelectedIndices.length, screenToWorld, shapes]);

  const handlePointerMove = useCallback((event) => {
    const interaction = interactionRef.current;
    const world = screenToWorld(event.clientX, event.clientY);

    if (currentTool === 'circle' && circleShape === 'circle' && circleMode === 'three-points' && !draftShape) {
      setHoverWorldPoint(world);
    } else if (hoverWorldPoint !== null) {
      setHoverWorldPoint(null);
    }

    if (
      currentTool === 'circle' &&
      draftShape?.type === 'circle' &&
      draftShape.construction === 'center-circumference' &&
      !interaction.start
    ) {
      setDraftShape((prev) => (
        prev?.type === 'circle' && prev.construction === 'center-circumference'
          ? {
              ...prev,
              previewPoint: world,
              radius: prev.center ? distanceBetween(prev.center, world) : 0,
            }
          : prev
      ));
      return;
    }

    if (
      currentTool === 'circle' &&
      draftShape?.type === 'circle' &&
      draftShape.construction === 'three-points' &&
      !interaction.start
    ) {
      setDraftShape((prev) => (
        prev?.type === 'circle' && prev.construction === 'three-points'
          ? { ...prev, previewPoint: world }
          : prev
      ));
      return;
    }

    if (currentTool === 'angle' && draftShape?.type === 'angle' && !interaction.start) {
      setDraftShape((prev) => (
        prev?.type === 'angle'
          ? { ...prev, previewPoint: world }
          : prev
      ));
      return;
    }

    if (!interaction.start) return;

    if (interaction.mode === 'panning') {
      const dx = event.clientX - interaction.startClient.x;
      const dy = event.clientY - interaction.startClient.y;
      setPan({ x: interaction.startPan.x + dx, y: interaction.startPan.y + dy });
      return;
    }

    if (interaction.mode === 'drawing-shape') {
      setDraftShape((prev) => (prev ? { ...prev, end: world } : prev));
      return;
    }

    if (interaction.mode === 'drawing-circle-center') {
      setDraftShape((prev) => (
        prev?.type === 'circle'
          ? { ...prev, radius: distanceBetween(prev.center, world), previewPoint: world }
          : prev
      ));
      return;
    }

    if (interaction.mode === 'drawing-pen') {
      setDraftShape((prev) => {
        if (!prev) return prev;
        return { ...prev, path: [...prev.path, world] };
      });
      return;
    }

    if (interaction.mode === 'moving-selection' && safeSelectedIndices.length) {
      const deltaX = world.x - interaction.selectedShapeStart.x;
      const deltaY = world.y - interaction.selectedShapeStart.y;
      setShapes((prev) => prev.map((shape, idx) => {
        if (!safeSelectedIndices.includes(idx)) return shape;
        const moved = cloneShapes([shape])[0];
        if (moved.start) {
          moved.start.x += deltaX;
          moved.start.y += deltaY;
        }
        if (moved.end) {
          moved.end.x += deltaX;
          moved.end.y += deltaY;
        }
        if (moved.vertex) {
          moved.vertex.x += deltaX;
          moved.vertex.y += deltaY;
        }
        if (moved.type === 'circle' && moved.center) {
          moved.center.x += deltaX;
          moved.center.y += deltaY;
          if (Array.isArray(moved.sourcePoints)) {
            moved.sourcePoints = moved.sourcePoints.map((point) => ({ x: point.x + deltaX, y: point.y + deltaY }));
          }
        }
        if (moved.type === 'angle' && Array.isArray(moved.sourcePoints)) {
          moved.sourcePoints = moved.sourcePoints.map((point) => ({ x: point.x + deltaX, y: point.y + deltaY }));
        }
        if (moved.pos) {
          moved.pos.x += deltaX;
          moved.pos.y += deltaY;
        }
        if (moved.type === 'math') {
          moved.x += deltaX;
          moved.y += deltaY;
        }
        if (moved.type === 'paragraph') {
          moved.x += deltaX;
          moved.y += deltaY;
        }
        if (moved.path) {
          moved.path = moved.path.map((point) => ({ x: point.x + deltaX, y: point.y + deltaY }));
        }
        if (moved.type === 'image') {
          moved.x += deltaX;
          moved.y += deltaY;
        }
        return moved;
      }));
      interaction.selectedShapeStart = world;
      return;
    }

    if (interaction.mode === 'resizing-selection' && interaction.selectionBoundsStart && interaction.selectionResizeAnchor) {
      const fromBounds = interaction.selectionBoundsStart;
      const anchor = interaction.selectionResizeAnchor;
      const dx = world.x - anchor.x;
      const dy = world.y - anchor.y;
      const sx = Math.abs(dx) / Math.max(fromBounds.width, 1e-9);
      const sy = Math.abs(dy) / Math.max(fromBounds.height, 1e-9);
      const uniformScale = Math.max(0.05, Math.max(sx, sy));
      const nextWidth = fromBounds.width * uniformScale;
      const nextHeight = fromBounds.height * uniformScale;
      const nextBounds = {
        x: dx < 0 ? anchor.x - nextWidth : anchor.x,
        y: dy < 0 ? anchor.y - nextHeight : anchor.y,
        width: nextWidth,
        height: nextHeight,
      };
      setShapes(transformSelectionShapes(interaction.initialShapes || shapes, safeSelectedIndices, fromBounds, nextBounds));
      return;
    }

    if (interaction.mode === 'moving' && selectedIndices.length) {
      const deltaX = world.x - interaction.selectedShapeStart.x;
      const deltaY = world.y - interaction.selectedShapeStart.y;
      setShapes((prev) => prev.map((shape, idx) => {
        if (!selectedIndices.includes(idx)) return shape;
        const moved = cloneShapes([shape])[0];
        if (moved.start) {
          moved.start.x += deltaX;
          moved.start.y += deltaY;
        }
        if (moved.end) {
          moved.end.x += deltaX;
          moved.end.y += deltaY;
        }
        if (moved.vertex) {
          moved.vertex.x += deltaX;
          moved.vertex.y += deltaY;
        }
        if (moved.type === 'circle' && moved.center) {
          moved.center.x += deltaX;
          moved.center.y += deltaY;
          if (Array.isArray(moved.sourcePoints)) {
            moved.sourcePoints = moved.sourcePoints.map((point) => ({ x: point.x + deltaX, y: point.y + deltaY }));
          }
        }
        if (moved.type === 'angle' && Array.isArray(moved.sourcePoints)) {
          moved.sourcePoints = moved.sourcePoints.map((point) => ({ x: point.x + deltaX, y: point.y + deltaY }));
        }
        if (moved.pos) {
          moved.pos.x += deltaX;
          moved.pos.y += deltaY;
        }
        if (moved.type === 'math') {
          moved.x += deltaX;
          moved.y += deltaY;
        }
        if (moved.type === 'paragraph') {
          moved.x += deltaX;
          moved.y += deltaY;
        }
        if (moved.path) {
          moved.path = moved.path.map((p) => ({ x: p.x + deltaX, y: p.y + deltaY }));
        }
        if (moved.type === 'image') {
          moved.x += deltaX;
          moved.y += deltaY;
        }
        return moved;
      }));
      interaction.selectedShapeStart = world;
      return;
    }

    if (interaction.mode === 'moving-line-endpoint' && interaction.draggedLineHandle) {
      const { shapeIndex, endpoint } = interaction.draggedLineHandle;
      setShapes((prev) => prev.map((shape, idx) => {
        if (idx !== shapeIndex || shape.type !== 'line') return shape;
        if (endpoint === 'start') return { ...shape, start: world };
        return { ...shape, end: world };
      }));
      return;
    }

    if (interaction.mode === 'resizing-image' && interaction.draggedImageHandle) {
      const { shapeIndex, handle } = interaction.draggedImageHandle;
      setShapes((prev) => prev.map((shape, idx) => {
        if (idx !== shapeIndex || shape.type !== 'image') return shape;

        const left = handle.includes('w') ? Math.min(world.x, shape.x + shape.width - IMAGE_MIN_SIZE) : shape.x;
        const top = handle.includes('n') ? Math.min(world.y, shape.y + shape.height - IMAGE_MIN_SIZE) : shape.y;
        const right = handle.includes('e') ? Math.max(world.x, shape.x + IMAGE_MIN_SIZE) : shape.x + shape.width;
        const bottom = handle.includes('s') ? Math.max(world.y, shape.y + IMAGE_MIN_SIZE) : shape.y + shape.height;

        return {
          ...shape,
          x: left,
          y: top,
          width: Math.max(IMAGE_MIN_SIZE, right - left),
          height: Math.max(IMAGE_MIN_SIZE, bottom - top),
        };
      }));
      return;
    }

    if (interaction.mode === 'resizing-paragraph-width' && interaction.draggedParagraphHandle) {
      const { shapeIndex, handle } = interaction.draggedParagraphHandle;
      setShapes((prev) => prev.map((shape, idx) => {
        if (idx !== shapeIndex || shape.type !== 'paragraph') return shape;

        const minWidth = PARAGRAPH_MIN_WIDTH / Math.max(zoom, 1e-9);
        if (handle === 'right') {
          return {
            ...shape,
            width: Math.max(minWidth, world.x - shape.x),
          };
        }

        const nextRight = shape.x + shape.width;
        const nextLeft = Math.min(world.x, nextRight - minWidth);
        return {
          ...shape,
          x: nextLeft,
          width: Math.max(minWidth, nextRight - nextLeft),
        };
      }));
      return;
    }

    if (interaction.mode === 'selecting') {
      setSelectionRect((prev) => (prev ? {
        ...prev,
        width: world.x - prev.x,
        height: world.y - prev.y,
      } : prev));
    }
  }, [circleMode, circleShape, currentTool, draftShape, hoverWorldPoint, safeSelectedIndices, screenToWorld, selectedIndices, shapes, transformSelectionShapes]);

  const handlePointerUp = useCallback(() => {
    const interaction = interactionRef.current;

    if (interaction.mode === 'drawing-shape' && draftShape) {
      commitShapes([...shapes, draftShape]);
      setDraftShape(null);
    }

    if (interaction.mode === 'drawing-circle-center' && draftShape?.type === 'circle') {
      finalizeCircle({
        center: draftShape.center,
        radius: draftShape.radius,
        strokeWidth: draftShape.strokeWidth || 1.5,
        construction: draftShape.construction || circleMode,
        sourcePoints: draftShape.previewPoint ? [draftShape.center, draftShape.previewPoint] : [draftShape.center],
      });
      setDraftShape(null);
    }

    if (interaction.mode === 'drawing-pen' && draftShape?.path?.length > 1) {
      commitShapes([...shapes, draftShape]);
    }
    if (interaction.mode === 'drawing-shape' || interaction.mode === 'drawing-pen') setDraftShape(null);

    if (interaction.mode === 'moving' && interaction.initialShapes) {
      setHistory((prev) => [...prev, interaction.initialShapes]);
      setFuture([]);
    }

    if (interaction.mode === 'moving-selection' && interaction.initialShapes) {
      setHistory((prev) => [...prev, interaction.initialShapes]);
      setFuture([]);
    }

    if (interaction.mode === 'resizing-selection' && interaction.initialShapes) {
      setHistory((prev) => [...prev, interaction.initialShapes]);
      setFuture([]);
    }

    if (interaction.mode === 'moving-line-endpoint' && interaction.initialShapes) {
      setHistory((prev) => [...prev, interaction.initialShapes]);
      setFuture([]);
    }

    if (interaction.mode === 'resizing-image' && interaction.initialShapes) {
      setHistory((prev) => [...prev, interaction.initialShapes]);
      setFuture([]);
    }

    if (interaction.mode === 'resizing-paragraph-width' && interaction.initialShapes) {
      setHistory((prev) => [...prev, interaction.initialShapes]);
      setFuture([]);
    }

    if (interaction.mode === 'selecting' && selectionRect) {
      const rect = normalizeRect(selectionRect);
      const ctx = canvasRef.current.getContext('2d');
      const nextSelected = shapes
        .map((shape, index) => ({ index, bounds: getShapeBounds(shape, ctx) }))
        .filter(({ bounds }) => (
          pointInRect({ x: bounds.x, y: bounds.y }, rect) ||
          pointInRect({ x: bounds.x + bounds.width, y: bounds.y + bounds.height }, rect)
        ))
        .map(({ index }) => index);
      setSelectedIndices(nextSelected);
    }

    interaction.mode = null;
    interaction.start = null;
    interaction.startPan = null;
    interaction.startClient = null;
    interaction.initialShapes = null;
    interaction.selectedShapeStart = null;
    interaction.draggedLineHandle = null;
    interaction.draggedImageHandle = null;
    interaction.draggedParagraphHandle = null;
    interaction.selectionBoundsStart = null;
    interaction.selectionResizeHandle = null;
    interaction.selectionResizeAnchor = null;
  }, [circleMode, commitShapes, draftShape, finalizeCircle, selectionRect, shapes]);

  const handleUndo = useCallback(() => {
    if (!history.length) return;
    const previous = history[history.length - 1];
    setHistory((prev) => prev.slice(0, -1));
    setFuture((prev) => [...prev, cloneShapes(shapes)]);
    setShapes(cloneShapes(previous));
    setSelectedIndices([]);
  }, [history, shapes]);

  const handleRedo = useCallback(() => {
    if (!future.length) return;
    const next = future[future.length - 1];
    setFuture((prev) => prev.slice(0, -1));
    setHistory((prev) => [...prev, cloneShapes(shapes)]);
    setShapes(cloneShapes(next));
    setSelectedIndices([]);
  }, [future, shapes]);

  const handleDelete = useCallback(() => {
    if (!selectedIndices.length) return;
    const selectedSet = new Set(selectedIndices);
    const next = shapes.filter((_, idx) => !selectedSet.has(idx));
    commitShapes(next);
    setSelectedIndices([]);
  }, [commitShapes, selectedIndices, shapes]);

  const handleDuplicateSelection = useCallback(() => {
    if (readOnly || !safeSelectedIndices.length) return;

    const duplicates = safeSelectedIndices.map((idx) => offsetShape(shapes[idx], 20, 20));
    commitShapes([...shapes, ...duplicates]);
    setSelectedIndices(duplicates.map((_, idx) => shapes.length + idx));
  }, [commitShapes, readOnly, safeSelectedIndices, shapes]);

  const applyColorSelection = useCallback((color) => {
    setCurrentColor(color);
    if (paragraphDraft) {
      setParagraphDraft((prev) => (prev ? {
        ...prev,
        shape: {
          ...prev.shape,
          color,
        },
      } : prev));
    }
    if (!safeSelectedIndices.length) return;

    const selectedSet = new Set(safeSelectedIndices);
    const nextShapes = shapes.map((shape, index) => (
      selectedSet.has(index)
        ? { ...shape, color }
        : shape
    ));
    commitShapes(nextShapes);
  }, [commitShapes, paragraphDraft, safeSelectedIndices, shapes]);

  const updateCanvasBackgroundColor = useCallback((color) => {
    const normalized = normalizeCanvasBackgroundColor(color);
    setCanvasBackgroundColor(normalized);
    setCanvasBackgroundInput(normalized);
  }, []);

  const handleCanvasBackgroundInputChange = useCallback((value) => {
    setCanvasBackgroundInput(value);
    if (isValidCanvasBackgroundColor(value)) {
      setCanvasBackgroundColor(normalizeCanvasBackgroundColor(value));
    }
  }, []);

  const handleCanvasBackgroundInputBlur = useCallback(() => {
    setCanvasBackgroundInput((current) =>
      isValidCanvasBackgroundColor(current)
        ? normalizeCanvasBackgroundColor(current)
        : canvasBackgroundColor
    );
  }, [canvasBackgroundColor]);

  const handleLineStyleChange = useCallback((lineStyle) => {
    if (selectedLineIndex < 0) return;

    const nextShapes = shapes.map((shape, index) => (
      index === selectedLineIndex && shape.type === 'line'
        ? { ...shape, lineStyle }
        : shape
    ));

    commitShapes(nextShapes);
  }, [commitShapes, selectedLineIndex, shapes]);

  const handleLineArrowChange = useCallback((arrowMode) => {
    if (selectedLineIndex < 0) return;

    const nextShapes = shapes.map((shape, index) => (
      index === selectedLineIndex && shape.type === 'line'
        ? { ...shape, arrowMode }
        : shape
    ));

    commitShapes(nextShapes);
  }, [commitShapes, selectedLineIndex, shapes]);

  const handleLineMarkingChange = useCallback((segmentMarking) => {
    if (selectedLineIndex < 0) return;

    const nextShapes = shapes.map((shape, index) => (
      index === selectedLineIndex && shape.type === 'line'
        ? { ...shape, segmentMarking }
        : shape
    ));

    commitShapes(nextShapes);
  }, [commitShapes, selectedLineIndex, shapes]);

  const handleLineParallelMarkerChange = useCallback((parallelMarker) => {
    if (selectedLineIndex < 0) return;

    const nextShapes = shapes.map((shape, index) => (
      index === selectedLineIndex && shape.type === 'line'
        ? { ...shape, parallelMarker }
        : shape
    ));

    commitShapes(nextShapes);
  }, [commitShapes, selectedLineIndex, shapes]);

  const handleStrokeWidthChange = useCallback((strokeWidth) => {
    if (selectedStrokeShapeIndex < 0) return;

    const nextShapes = shapes.map((shape, index) => (
      index === selectedStrokeShapeIndex && (shape.type === 'line' || shape.type === 'circle' || shape.type === 'semicircle' || shape.type === 'angle')
        ? { ...shape, strokeWidth }
        : shape
    ));

    commitShapes(nextShapes);
  }, [commitShapes, selectedStrokeShapeIndex, shapes]);

  const handleAngleRadiusChange = useCallback((radius) => {
    if (selectedAngleIndex < 0) return;

    const nextRadius = Math.max(8, Number(radius) || 8);
    const nextShapes = shapes.map((shape, index) => (
      index === selectedAngleIndex && shape.type === 'angle'
        ? { ...shape, radius: nextRadius }
        : shape
    ));

    commitShapes(nextShapes);
  }, [commitShapes, selectedAngleIndex, shapes]);

  const handleAngleShadeChange = useCallback((shade) => {
    if (selectedAngleIndex < 0) return;

    const nextShade = Math.max(0.05, Math.min(0.7, Number(shade) || 0.18));
    const nextShapes = shapes.map((shape, index) => (
      index === selectedAngleIndex && shape.type === 'angle'
        ? { ...shape, shade: nextShade }
        : shape
    ));

    commitShapes(nextShapes);
  }, [commitShapes, selectedAngleIndex, shapes]);

  const handleAngleReflexToggle = useCallback(() => {
    if (selectedAngleIndex < 0) return;

    const nextShapes = shapes.map((shape, index) => (
      index === selectedAngleIndex && shape.type === 'angle'
        ? { ...shape, useReflex: !shape.useReflex }
        : shape
    ));

    commitShapes(nextShapes);
  }, [commitShapes, selectedAngleIndex, shapes]);

  const handleClear = useCallback(() => {
    if (!shapes.length) return;
    commitShapes([]);
    setSelectedIndices([]);
    setSelectionRect(null);
  }, [commitShapes, shapes.length]);

  const handleExport = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const renderCanvasEl = document.createElement('canvas');
    renderCanvasEl.width = canvas.width;
    renderCanvasEl.height = canvas.height;
    const renderCtx = renderCanvasEl.getContext('2d');
    const temp = document.createElement('canvas');
    const tempCtx = temp.getContext('2d');
    const sourceRect = selectionRect ? normalizeRect(selectionRect) : null;

    renderCanvas(renderCtx, renderCanvasEl, {
      includeSelectionUi: false,
      includeDraftShape: false,
    });

    if (sourceRect && sourceRect.width > 0 && sourceRect.height > 0) {
      const sx = sourceRect.x * zoom + pan.x;
      const sy = sourceRect.y * zoom + pan.y;
      const sw = sourceRect.width * zoom;
      const sh = sourceRect.height * zoom;
      temp.width = Math.max(1, Math.round(sw));
      temp.height = Math.max(1, Math.round(sh));
      tempCtx.fillStyle = canvasBackgroundColor;
      tempCtx.fillRect(0, 0, temp.width, temp.height);
      tempCtx.drawImage(renderCanvasEl, sx, sy, sw, sh, 0, 0, temp.width, temp.height);
    } else {
      temp.width = renderCanvasEl.width;
      temp.height = renderCanvasEl.height;
      tempCtx.drawImage(renderCanvasEl, 0, 0);
    }

    const link = document.createElement('a');
    link.download = 'geometry-tool-export.png';
    link.href = temp.toDataURL('image/png');
    link.click();
  }, [canvasBackgroundColor, pan.x, pan.y, renderCanvas, selectionRect, zoom]);

  const handleExportPdfHandout = useCallback(async () => {
    if (!user?.uid) {
      setSaveFeedback({ type: 'error', message: 'Sign in to export PDF handouts.' });
      return;
    }

    try {
      setIsExportingPdf(true);
      setSaveFeedback({ type: 'saving', message: 'Preparing PDF handout...' });
      const { slides: nextSlides } = buildSlidesSnapshot();

      const exportFn = httpsCallable(functions, 'exportGeometryHandoutPdf');
      const result = await exportFn({
        title: (diagramTitle || '').trim() || DEFAULT_DIAGRAM_TITLE,
        layout: pdfHandoutLayout,
        slides: nextSlides.map((slide, index) => ({
          title: slide.title?.trim() || getCanvasSlideTitle(index + 1),
          state: normalizeDiagramCanvasState(slide.state),
        })),
      });

      if (result.data?.pdf) {
        const blob = new Blob(
          [Uint8Array.from(atob(result.data.pdf), (char) => char.charCodeAt(0))],
          { type: 'application/pdf' }
        );
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = result.data.fileName || 'geometry-handout.pdf';
        link.click();
        URL.revokeObjectURL(url);
      }

      setSaveFeedback({ type: 'saved', message: 'PDF handout exported.' });
    } catch (error) {
      console.error('Failed to export geometry handout PDF:', error);
      setSaveFeedback({ type: 'error', message: error.message || 'Failed to export PDF handout.' });
    } finally {
      setIsExportingPdf(false);
    }
  }, [buildSlidesSnapshot, diagramTitle, pdfHandoutLayout, user?.uid]);

  const updateZoom = useCallback((nextZoom) => {
    const clamped = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, nextZoom));
    const center = { x: canvasSize.width / 2, y: canvasSize.height / 2 };
    const before = { x: (center.x - pan.x) / zoom, y: (center.y - pan.y) / zoom };
    const nextPan = { x: center.x - before.x * clamped, y: center.y - before.y * clamped };
    setZoom(clamped);
    setPan(nextPan);
  }, [canvasSize.height, canvasSize.width, pan.x, pan.y, zoom]);

  useEffect(() => {
    if (!initialDiagramData) return;
    applyDiagramState(initialDiagramData);
    setCurrentDiagramId(initialDiagramData?.id || '');
    setDiagramTitle(initialTitle || initialDiagramData?.title || DEFAULT_DIAGRAM_TITLE);
  }, [applyDiagramState, initialDiagramData, initialTitle]);

  useEffect(() => {
    if (!containerRef.current) return undefined;

    const updateCanvasSize = () => {
      if (!containerRef.current) return;
      const nextHeight = isFullscreenMode
        ? Math.max(CANVAS_HEIGHT, Math.floor(containerRef.current.clientHeight || CANVAS_HEIGHT))
        : CANVAS_HEIGHT;
      const nextWidth = CANVAS_WIDTH;

      setCanvasSize((current) => (
        current.width === nextWidth && current.height === nextHeight
          ? current
          : { width: nextWidth, height: nextHeight }
      ));
    };

    updateCanvasSize();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateCanvasSize) : null;
    if (observer) observer.observe(containerRef.current);
    if (!observer) window.addEventListener('resize', updateCanvasSize);

    return () => {
      if (observer) observer.disconnect();
      else window.removeEventListener('resize', updateCanvasSize);
    };
  }, [isFullscreenMode]);

  useEffect(() => {
    if (!teachingMode || typeof document === 'undefined') return undefined;

    const root = fullScreenRootRef.current;
    const requestFullScreen = root?.requestFullscreen?.bind(root)
      || root?.webkitRequestFullscreen?.bind(root)
      || null;

    if (!requestFullScreen) return undefined;

    requestFullScreen().catch?.(() => {});

    return () => {
      if (document.fullscreenElement === root) {
        document.exitFullscreen?.().catch?.(() => {});
      } else if (document.webkitFullscreenElement === root) {
        document.webkitExitFullscreen?.();
      }
    };
  }, [teachingMode]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const root = fullScreenRootRef.current;
    const handleFullscreenChange = () => {
      const activeElement = document.fullscreenElement || document.webkitFullscreenElement || null;
      if (activeElement !== root) {
        setTeachingMode(false);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    const handleUp = () => handlePointerUp();
    window.addEventListener('pointerup', handleUp);
    return () => window.removeEventListener('pointerup', handleUp);
  }, [handlePointerUp]);

  useEffect(() => {
    if (!paragraphDraft) return undefined;

    const editor = paragraphEditorRef.current;
    if (!editor || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(() => {
      syncParagraphDraftFromEditor();
    });
    observer.observe(editor);
    return () => observer.disconnect();
  }, [paragraphDraft, syncParagraphDraftFromEditor]);

  useEffect(() => {
    if (!paragraphDraft || paragraphMathDraft) return undefined;

    const handlePointerDown = (event) => {
      if (!paragraphOutsideCloseReadyRef.current) return;
      if (paragraphEditorRef.current?.contains(event.target)) return;
      if (paragraphToolbarRef.current?.contains(event.target)) return;
      finalizeParagraph();
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [finalizeParagraph, paragraphDraft, paragraphMathDraft]);

  useEffect(() => {
    if (!paragraphDraft) return undefined;
    const editor = paragraphEditorRef.current;
    if (!editor) return undefined;

    editor.innerHTML = paragraphDraft.shape.html || '<p><br></p>';
    renderParagraphMath(editor).catch(() => {});
    requestAnimationFrame(() => {
      editor.focus();
      saveParagraphSelection();
    });
    return undefined;
  }, [editingParagraphKey, renderParagraphMath, saveParagraphSelection]);

  useEffect(() => {
    if (paragraphDraft) return undefined;
    paragraphOutsideCloseReadyRef.current = false;
    return undefined;
  }, [paragraphDraft]);

  useEffect(() => {
    const refs = paragraphDisplayRefs.current;
    safeShapes.forEach((shape, index) => {
      if (shape?.type !== 'paragraph') return;
      if (paragraphDraft?.shapeIndex === index) return;
      const node = refs.get(index);
      if (node) {
        node.innerHTML = shape.html || '';
        renderParagraphMath(node).catch(() => {});
      }
    });
  }, [paragraphDraft?.shapeIndex, renderParagraphMath, safeShapes]);

  useEffect(() => {
    const onPaste = (event) => {
      const active = document.activeElement;
      const isToolFocused = active === containerRef.current || containerRef.current?.contains(active);
      if (!isToolFocused) return;

      const target = event.target;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target?.isContentEditable
      ) {
        return;
      }

      const file = Array.from(event.clipboardData?.items || [])
        .find((item) => item.type.startsWith('image/'))
        ?.getAsFile();

      if (!file) return;

      event.preventDefault();
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') addImageFromDataUrl(reader.result);
      };
      reader.readAsDataURL(file);
    };

    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [addImageFromDataUrl]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (readOnly) return;
      if (paragraphDraft) {
        if (event.key === 'Escape') {
          event.preventDefault();
          cancelParagraph();
        }
        if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'b') {
          event.preventDefault();
          applyParagraphCommand('bold');
        }
        if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'i') {
          event.preventDefault();
          applyParagraphCommand('italic');
        }
        if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'u') {
          event.preventDefault();
          applyParagraphCommand('underline');
        }
        if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'm') {
          event.preventDefault();
          const button = paragraphToolbarRef.current?.querySelector('[data-paragraph-math-button="true"]');
          if (button) {
            handleParagraphMathButtonClick({ currentTarget: button });
          }
        }
      }

      if (event.key === 'Delete') {
        event.preventDefault();
        handleDelete();
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (event.shiftKey) {
          handleRedo();
        } else {
          handleUndo();
        }
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'd' && safeSelectedIndices.length) {
        event.preventDefault();
        handleDuplicateSelection();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [applyParagraphCommand, cancelParagraph, handleDelete, handleDuplicateSelection, handleParagraphMathButtonClick, handleRedo, handleUndo, paragraphDraft, readOnly, safeSelectedIndices.length]);

  useEffect(() => {
    if (isLibraryOpen) {
      setIsLibraryLoading(true);
      Promise.all([
        loadSavedDiagrams(),
        loadSharedDiagrams(),
      ]).finally(() => setIsLibraryLoading(false));
    }
  }, [isLibraryOpen, loadSavedDiagrams, loadSharedDiagrams]);

  useEffect(() => {
    if (!openLibrarySignal) return;
    setIsLibraryOpen(true);
  }, [openLibrarySignal]);

  useEffect(() => {
    if (!newCanvasSignal) return;
    startNewCanvas();
  }, [newCanvasSignal, startNewCanvas]);

  useEffect(() => {
    if (!isCircleMenuOpen || typeof document === 'undefined') return undefined;

    const handlePointerDown = (event) => {
      if (circleMenuRef.current?.contains(event.target)) return;
      setIsCircleMenuOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isCircleMenuOpen]);

  const canDuplicate = safeSelectedIndices.length > 0;
  const canDelete = safeSelectedIndices.length > 0;
  const selectedToolMeta = useMemo(() => TOOL_OPTIONS.find((tool) => tool.id === currentTool), [currentTool]);
  const selectedLineStyle = selectedLineIndex >= 0 ? (safeShapes[selectedLineIndex]?.lineStyle || 'solid') : 'solid';
  const selectedLineArrowMode = selectedLineIndex >= 0 ? (safeShapes[selectedLineIndex]?.arrowMode || 'none') : 'none';
  const selectedLineMarking = selectedLineIndex >= 0 ? (safeShapes[selectedLineIndex]?.segmentMarking || 'none') : 'none';
  const selectedLineParallelMarker = selectedLineIndex >= 0 ? (safeShapes[selectedLineIndex]?.parallelMarker || 'none') : 'none';
  const selectedStrokeWidth = selectedStrokeShapeIndex >= 0 ? (safeShapes[selectedStrokeShapeIndex]?.strokeWidth || 1.5) : 1.5;
  const selectedAngleRadius = selectedAngleIndex >= 0 ? (safeShapes[selectedAngleIndex]?.radius || 52) : 52;
  const selectedAngleShade = selectedAngleIndex >= 0 ? (safeShapes[selectedAngleIndex]?.shade ?? 0.18) : 0.18;
  const selectedAngleUseReflex = selectedAngleIndex >= 0 ? Boolean(safeShapes[selectedAngleIndex]?.useReflex) : false;
  const worldRectToScreenRect = useCallback((shape) => ({
    left: (shape.x * zoom) + pan.x,
    top: (shape.y * zoom) + pan.y,
    width: shape.width * zoom,
    height: shape.height * zoom,
  }), [pan.x, pan.y, zoom]);
  const canvasCursor = currentTool === 'pan'
    ? 'grab'
    : currentTool === 'pointer'
      ? 'default'
      : currentTool === 'text' || currentTool === 'math' || currentTool === 'paragraph'
        ? 'text'
        : 'crosshair';

  return (
    <div
      ref={fullScreenRootRef}
      className={[
        'bg-white',
        isFullscreenMode ? 'flex h-full min-h-0 flex-col' : 'rounded-xl border border-gray-200 p-4 shadow-sm',
      ].join(' ')}
    >
      <div className="mb-2 flex items-center justify-between">
        {!isFullscreenMode ? (
          <div>
            <h2 className="text-base font-semibold text-gray-900">Canvas</h2>
            Active: <span className="font-medium text-gray-700">{selectedToolMeta?.label || 'Tool'}</span>
          </div>
        ) : (
          <div />
        )}
      </div>

      {showSaveBar && (
      <div className={['mb-3 rounded-xl border border-gray-200 bg-white/90 p-3', isFullscreenMode ? 'mx-3 mt-3' : ''].join(' ')}>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center">
            <input
              type="text"
              value={diagramTitle}
              onChange={(event) => setDiagramTitle(event.target.value)}
              placeholder={DEFAULT_DIAGRAM_TITLE}
              className="h-10 min-w-0 flex-1 rounded-lg border border-gray-300 px-3 text-sm text-gray-800 shadow-sm outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
            />
            <div className="flex flex-wrap gap-2">
              <select
                value={pdfHandoutLayout}
                onChange={(event) => setPdfHandoutLayout(event.target.value)}
                disabled={isExportingPdf || isSavingDiagram}
                className="h-10 rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-700 shadow-sm outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100 disabled:cursor-not-allowed disabled:opacity-50"
                title="Choose PDF handout layout"
              >
                <option value="portrait-2">A4 portrait · 2 slides</option>
                <option value="landscape-4">A4 landscape · 4 slides</option>
              </select>
              <button
                type="button"
                onClick={handleExportPdfHandout}
                disabled={isExportingPdf || isSavingDiagram}
                className="inline-flex h-10 items-center gap-2 rounded-lg border border-gray-300 px-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                title={user?.uid ? 'Export PDF handout' : 'Sign in to export PDF handouts'}
              >
                <Download className="h-4 w-4" />
                {isExportingPdf ? 'Exporting PDF...' : 'PDF handout'}
              </button>
              <button
                type="button"
                onClick={() => saveDiagram({ saveAsNew: true })}
                disabled={isSavingDiagram}
                className="inline-flex h-10 items-center gap-2 rounded-lg border border-gray-300 px-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                Save as new
              </button>
              <button
                type="button"
                onClick={() => saveDiagram()}
                disabled={isSavingDiagram}
                className="inline-flex h-10 items-center gap-2 rounded-lg bg-sky-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-sky-700 disabled:cursor-not-allowed disabled:bg-sky-300"
              >
                <Save className="h-4 w-4" />
                {currentDiagramId ? 'Update' : 'Save'}
              </button>
              {user?.uid && (
                <button
                  type="button"
                  onClick={() => {
                    if (!canShareContent(userData)) {
                      setSaveFeedback({ type: 'error', message: 'Sharing saved diagrams requires the Basic or Pro plan.' });
                      return;
                    }
                    setIsShareModalOpen(true);
                  }}
                  disabled={!currentDiagramId || isSavingDiagram}
                  className="inline-flex h-10 items-center gap-2 rounded-lg border border-gray-300 px-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  title={currentDiagramId ? 'Share this saved diagram' : 'Save this diagram before sharing'}
                >
                  <Share2 className="h-4 w-4" />
                  Share
                </button>
              )}
            </div>
          </div>
          <div className="text-xs text-gray-500">
            {saveFeedback.message || 'Use the tool during your trial. Upgrade to Basic or Pro when you need to save or share diagrams.'}
          </div>
        </div>
      </div>
      )}

      <div className={['flex flex-col lg:flex-row', isFullscreenMode ? 'min-h-0 flex-1 gap-3' : 'gap-4'].join(' ')}>
        {!readOnly && (
        <div className={['w-full lg:w-[120px] flex lg:flex-col gap-3', isFullscreenMode ? 'min-h-0 self-stretch overflow-y-auto pr-1' : 'lg:max-h-[500px] lg:self-start lg:overflow-y-auto lg:pr-1'].join(' ')}>
          <div className="grid grid-cols-4 lg:grid-cols-2 gap-2">
            {TOOL_OPTIONS.map(({ id, label, icon: Icon }) => (
              <div key={id} className="relative" ref={id === 'circle' ? circleMenuRef : null}>
                <button
                  type="button"
                  title={label}
                  onClick={() => {
                    if (paragraphDraft) finalizeParagraph();
                    setCurrentTool(id);
                    setDraftShape(null);
                    setHoverWorldPoint(null);
                    if (id !== 'select') setSelectionRect(null);
                    setIsCircleMenuOpen(id === 'circle' ? (currentTool === 'circle' ? !isCircleMenuOpen : true) : false);
                  }}
                  className={[
                    'h-10 w-10 rounded-lg border flex items-center justify-center transition-colors',
                    currentTool === id ? 'border-sky-600 bg-sky-50 text-sky-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50',
                  ].join(' ')}
                >
                  <Icon className="w-4 h-4" />
                </button>
                {id === 'circle' && isCircleMenuOpen && (
                  <div className="absolute left-0 top-full z-40 mt-2 min-w-[11rem] rounded-lg border border-gray-200 bg-white p-2 shadow-xl">
                    <div className="mb-2 text-[11px] font-medium uppercase tracking-wide text-gray-500">
                      Shape
                    </div>
                    <div className="flex flex-col gap-1">
                      {CIRCLE_SHAPE_OPTIONS.map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => {
                            setCircleShape(option.id);
                            setCircleMode(option.id === 'circle' ? 'center-radius' : 'diameter-drag');
                            setDraftShape(null);
                            setHoverWorldPoint(null);
                          }}
                          className={[
                            'rounded border px-2 py-1 text-left text-xs transition-colors',
                            circleShape === option.id
                              ? 'border-sky-600 bg-sky-50 text-sky-700'
                              : 'border-gray-300 text-gray-600 hover:bg-gray-50',
                          ].join(' ')}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                    <div className="mb-2 mt-3 text-[11px] font-medium uppercase tracking-wide text-gray-500">
                      {circleShape === 'circle' ? 'Circle Mode' : 'Semicircle Mode'}
                    </div>
                    <div className="flex flex-col gap-1">
                      {(circleShape === 'circle' ? CIRCLE_MODE_OPTIONS : SEMICIRCLE_MODE_OPTIONS).map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => {
                            setCircleMode(option.id);
                            setDraftShape(null);
                            setHoverWorldPoint(null);
                            setIsCircleMenuOpen(false);
                          }}
                          className={[
                            'rounded border px-2 py-1 text-left text-xs transition-colors',
                            circleMode === option.id
                              ? 'border-sky-600 bg-sky-50 text-sky-700'
                              : 'border-gray-300 text-gray-600 hover:bg-gray-50',
                          ].join(' ')}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="space-y-2 w-full">
            <div className="grid grid-cols-4 lg:grid-cols-2 gap-1.5">
              {COLOR_TABS.map((color) => (
                <button
                  key={color}
                  type="button"
                  title={color}
                  onClick={() => applyColorSelection(color)}
                  className={[
                    'h-7 rounded border',
                    currentColor === color ? 'border-sky-600 ring-1 ring-sky-300' : 'border-gray-300',
                  ].join(' ')}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
            <input
              value={currentColor}
              onChange={(e) => applyColorSelection(e.target.value)}
              className="w-full h-8 px-2 rounded border border-gray-300 text-xs font-mono"
            />
            <input
              type="color"
              value={currentColor}
              onChange={(e) => applyColorSelection(e.target.value)}
              className="w-full h-8 rounded border border-gray-300"
            />
            <div className="space-y-1 rounded-lg border border-gray-200 bg-white p-2">
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Canvas background</p>
              <div className="flex items-center gap-1.5">
                <input
                  type="color"
                  value={getColorInputValue(canvasBackgroundColor)}
                  onChange={(event) => updateCanvasBackgroundColor(event.target.value)}
                  className="h-8 w-8 shrink-0 cursor-pointer rounded border border-gray-300 bg-white p-1"
                  aria-label="Pick canvas background color"
                  title="Pick canvas background color"
                />
                <input
                  type="text"
                  value={canvasBackgroundInput}
                  onChange={(event) => handleCanvasBackgroundInputChange(event.target.value)}
                  onBlur={handleCanvasBackgroundInputBlur}
                  placeholder="#FFFFFF or rgb(255, 255, 255)"
                  className="min-w-0 flex-1 rounded border border-gray-300 px-2 py-1.5 text-xs font-mono"
                  aria-label="Canvas background color"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-1">
              {[16, 21, 28].map((size) => (
                <button
                  key={size}
                  type="button"
                  onClick={() => {
                    setCurrentTextSize(size);
                    if (paragraphDraft) {
                      setParagraphDraft((prev) => (prev ? {
                        ...prev,
                        shape: {
                          ...prev.shape,
                          size,
                        },
                      } : prev));
                    }
                  }}
                  className={[
                    'h-7 rounded border text-[11px]',
                    currentTextSize === size ? 'border-sky-600 bg-sky-50 text-sky-700' : 'border-gray-300 text-gray-600',
                  ].join(' ')}
                >
                  {size === 16 ? 'S' : size === 21 ? 'M' : 'L'}
                </button>
              ))}
            </div>
          </div>
        </div>
        )}

        <div className={['flex flex-1 gap-3 overflow-x-auto', isFullscreenMode ? 'min-h-0' : ''].join(' ')}>
          <div
            ref={containerRef}
            tabIndex={0}
            className={[
              'relative rounded-lg border border-gray-200 bg-gray-50 overflow-hidden focus:outline-none focus:ring-2 focus:ring-sky-200',
              isFullscreenMode ? 'min-h-0 self-stretch' : '',
            ].join(' ')}
            style={{
              width: CANVAS_WIDTH,
              minWidth: CANVAS_WIDTH,
              height: isFullscreenMode ? '100%' : CANVAS_HEIGHT,
            }}
          >
            <canvas
              ref={canvasRef}
              width={canvasSize.width}
              height={canvasSize.height}
              className="h-full w-full cursor-crosshair touch-none"
              style={{ cursor: canvasCursor, backgroundColor: canvasBackgroundColor }}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerLeave={handlePointerUp}
              onDoubleClick={handleCanvasDoubleClick}
              onContextMenu={(e) => e.preventDefault()}
            />

            {safeShapes.map((shape, index) => {
              if (shape?.type !== 'paragraph' || paragraphDraft?.shapeIndex === index) return null;
              const rect = worldRectToScreenRect(shape);
              return (
                <div
                  key={`paragraph-${index}`}
                  ref={(node) => {
                    if (node) paragraphDisplayRefs.current.set(index, node);
                    else paragraphDisplayRefs.current.delete(index);
                  }}
                  className="absolute overflow-visible px-3 py-2"
                  style={{
                    left: rect.left,
                    top: rect.top,
                    width: rect.width,
                    height: rect.height,
                    color: shape.color || currentColor,
                    fontSize: `${shape.size || currentTextSize}px`,
                    lineHeight: 1.5,
                    textAlign: shape.textAlign || 'left',
                    pointerEvents: currentTool === 'paragraph' ? 'auto' : 'none',
                  }}
                  onMouseDown={(event) => {
                    if (currentTool !== 'paragraph') return;
                    event.preventDefault();
                    beginParagraphInput({ x: shape.x, y: shape.y }, index);
                  }}
                />
              );
            })}

            {paragraphDraft && (
              <>
                {(() => {
                  const { viewportWidth: maxVisibleWidth, viewportHeight: maxVisibleHeight, maxEditorWidth, maxEditorHeight } = getParagraphEditorMetrics();
                  const editorWidth = Math.min(paragraphDraft.shape.width * zoom, maxEditorWidth);
                  const editorHeight = Math.min(paragraphDraft.shape.height * zoom, maxEditorHeight);
                  const rawEditorLeft = (paragraphDraft.shape.x * zoom) + pan.x;
                  const rawEditorTop = (paragraphDraft.shape.y * zoom) + pan.y;
                  const maxEditorLeft = Math.max(PARAGRAPH_VIEWPORT_PADDING, maxVisibleWidth - editorWidth + PARAGRAPH_VIEWPORT_PADDING);
                  const maxEditorTop = Math.max(PARAGRAPH_VIEWPORT_PADDING, maxVisibleHeight - editorHeight + PARAGRAPH_VIEWPORT_PADDING);
                  const editorLeft = Math.min(
                    Math.max(PARAGRAPH_VIEWPORT_PADDING, rawEditorLeft),
                    maxEditorLeft
                  );
                  const editorTop = Math.min(
                    Math.max(PARAGRAPH_VIEWPORT_PADDING, rawEditorTop),
                    maxEditorTop
                  );
                  const toolbarTop = editorTop >= (PARAGRAPH_TOOLBAR_HEIGHT + PARAGRAPH_TOOLBAR_GAP + PARAGRAPH_VIEWPORT_PADDING)
                    ? editorTop - PARAGRAPH_TOOLBAR_HEIGHT - PARAGRAPH_TOOLBAR_GAP
                    : Math.min(
                        maxVisibleHeight - PARAGRAPH_TOOLBAR_HEIGHT + PARAGRAPH_VIEWPORT_PADDING,
                        editorTop + editorHeight + PARAGRAPH_TOOLBAR_GAP
                      );
                  const editorMinWidth = Math.min(PARAGRAPH_MIN_WIDTH, maxEditorWidth);
                  const editorMinHeight = Math.min(PARAGRAPH_MIN_HEIGHT, maxEditorHeight);
                  return (
              <>
                <div
                  ref={paragraphToolbarRef}
                  className="absolute z-30 flex items-center gap-1 rounded-xl border border-gray-200 bg-white/95 p-2 shadow-xl backdrop-blur"
                  style={{
                    left: Math.max(12, editorLeft),
                    top: toolbarTop,
                  }}
                >
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => applyParagraphCommand('bold')} className="rounded border border-gray-300 p-1.5 text-gray-700 hover:bg-gray-50" title="Bold">
                    <Bold className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => applyParagraphCommand('italic')} className="rounded border border-gray-300 p-1.5 text-gray-700 hover:bg-gray-50" title="Italic">
                    <Italic className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => applyParagraphCommand('underline')} className="rounded border border-gray-300 p-1.5 text-gray-700 hover:bg-gray-50" title="Underline">
                    <Underline className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => applyParagraphAlignment('left')} className="rounded border border-gray-300 p-1.5 text-gray-700 hover:bg-gray-50" title="Align left">
                    <AlignLeft className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => applyParagraphAlignment('center')} className="rounded border border-gray-300 p-1.5 text-gray-700 hover:bg-gray-50" title="Align center">
                    <AlignCenter className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => applyParagraphAlignment('right')} className="rounded border border-gray-300 p-1.5 text-gray-700 hover:bg-gray-50" title="Align right">
                    <AlignRight className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => applyParagraphAlignment('justify')} className="rounded border border-gray-300 p-1.5 text-gray-700 hover:bg-gray-50" title="Justify">
                    <AlignJustify className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    data-paragraph-math-button="true"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={handleParagraphMathButtonClick}
                    className="rounded border border-gray-300 p-1.5 text-gray-700 hover:bg-gray-50"
                    title="Insert math"
                  >
                    <Sigma className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={cancelParagraph} className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50">
                    Cancel
                  </button>
                </div>

                <div
                  ref={paragraphEditorRef}
                  contentEditable
                  suppressContentEditableWarning
                  onInput={syncParagraphDraftFromEditor}
                  onKeyUp={saveParagraphSelection}
                  onMouseUp={saveParagraphSelection}
                  onBlur={saveParagraphSelection}
                  onDoubleClick={handleParagraphMathDoubleClick}
                  className="absolute z-20 overflow-hidden whitespace-pre-wrap rounded-md border-2 border-sky-500 bg-white px-3 py-2 shadow-lg outline-none"
                  style={{
                    left: editorLeft,
                    top: editorTop,
                    width: editorWidth,
                    height: editorHeight,
                    boxSizing: 'border-box',
                    minWidth: editorMinWidth,
                    minHeight: editorMinHeight,
                    maxWidth: maxEditorWidth,
                    maxHeight: maxEditorHeight,
                    resize: 'none',
                    color: paragraphDraft.shape.color || currentColor,
                    fontSize: `${paragraphDraft.shape.size || currentTextSize}px`,
                    lineHeight: 1.5,
                    textAlign: paragraphDraft.shape.textAlign || 'left',
                  }}
                />
              </>
                  );
                })()}
              </>
            )}

            {(!readOnly || allowTeachingModeInReadOnly) && (
              <button
                type="button"
                onClick={() => setTeachingMode((current) => !current)}
                className={[
                  'absolute left-1 top-1 z-30 inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white/95 px-3 py-2 text-sm font-medium shadow-lg backdrop-blur transition-colors',
                  teachingMode ? 'border-sky-600 bg-sky-50/95 text-sky-800' : 'text-gray-700 hover:bg-gray-50',
                ].join(' ')}
              >
                <PenTool className="h-4 w-4" />
                {teachingMode ? 'Exit teaching mode' : 'Teaching mode'}
              </button>
            )}

            {textDraft && (
              <input
                ref={textInputRef}
                type="text"
                value={textDraft.text}
                onChange={(e) => setTextDraft((prev) => ({ ...prev, text: e.target.value }))}
                onBlur={finalizeText}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') finalizeText();
                  if (e.key === 'Escape') setTextDraft(null);
                }}
                className="absolute z-10 h-8 px-2 text-sm border border-gray-300 rounded bg-white"
                style={{ left: textDraft.left, top: textDraft.top, minWidth: 160 }}
              />
            )}

            <InlineMathModal
              isOpen={Boolean(mathDraft)}
              initialLatex={mathDraft?.initialLatex || ''}
              anchorRect={mathDraft?.anchorRect || null}
              onInsert={finalizeMath}
              onClose={() => setMathDraft(null)}
            />

            <InlineMathModal
              isOpen={Boolean(paragraphMathDraft)}
              initialLatex={paragraphMathDraft?.initialLatex || ''}
              anchorRect={paragraphMathDraft?.anchorRect || null}
              onInsert={insertParagraphMath}
              onClose={() => setParagraphMathDraft(null)}
            />

            <div className="absolute right-3 top-3 flex gap-1.5">
              <button
                type="button"
                onClick={() => updateZoom(zoom + ZOOM_STEP)}
                className="w-8 h-8 rounded-full bg-white border border-gray-300 shadow-sm flex items-center justify-center"
                title="Zoom In"
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => updateZoom(zoom - ZOOM_STEP)}
                className="w-8 h-8 rounded-full bg-white border border-gray-300 shadow-sm flex items-center justify-center"
                title="Zoom Out"
              >
                <Minus className="w-3.5 h-3.5" />
              </button>
            </div>

            <button
              type="button"
              onClick={() => {
                setZoom(1);
                setPan({ x: canvasSize.width / 2, y: canvasSize.height / 2 });
              }}
              className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-white border border-gray-300 shadow-sm flex items-center justify-center"
              title="Home View"
            >
              <Home className="w-4 h-4" />
            </button>

            <div className="absolute bottom-1 left-3 z-30 inline-flex items-center gap-1 rounded-tr-xl border border-gray-200 bg-white/95 p-1 shadow-lg backdrop-blur">
              <button
                type="button"
                onClick={() => activeSlideIndex > 0 && switchToSlide(safeSlides[activeSlideIndex - 1]?.id)}
                disabled={activeSlideIndex <= 0}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="Previous canvas"
                title="Previous canvas"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <div
                className="min-w-[2.5rem] px-1 text-center text-xs font-semibold text-gray-700"
                aria-label={`Canvas ${activeSlideIndex >= 0 ? activeSlideIndex + 1 : 1} of ${safeSlides.length || 1}`}
              >
                {activeSlideIndex >= 0 ? activeSlideIndex + 1 : 1}
              </div>
              <button
                type="button"
                onClick={() => activeSlideIndex >= 0 && activeSlideIndex < safeSlides.length - 1 && switchToSlide(safeSlides[activeSlideIndex + 1]?.id)}
                disabled={activeSlideIndex < 0 || activeSlideIndex >= safeSlides.length - 1}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="Next canvas"
                title="Next canvas"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
              {!readOnly && (
                <>
                  <div className="mx-1 w-px self-stretch bg-gray-200" />
                  <button
                    type="button"
                    onClick={addSlide}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-600 transition-colors hover:bg-gray-100"
                    aria-label="Add canvas"
                    title="Add canvas"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={duplicateSlide}
                    disabled={!activeSlide}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="Duplicate canvas"
                    title="Duplicate canvas"
                  >
                    <CopyPlus className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => activeSlide && removeSlide(activeSlide.id)}
                    disabled={!activeSlide}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="Delete canvas"
                    title="Delete canvas"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveSlide('backward')}
                    disabled={activeSlideIndex <= 0}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="Move canvas backward"
                    title="Move canvas backward"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveSlide('forward')}
                    disabled={activeSlideIndex < 0 || activeSlideIndex >= safeSlides.length - 1}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="Move canvas forward"
                    title="Move canvas forward"
                  >
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </>
              )}
            </div>
          </div>

          {!readOnly && (
          <div className={['flex flex-col gap-2', isFullscreenMode ? 'min-h-0 self-stretch overflow-y-auto pr-1' : 'mt-1 max-h-[500px] self-start overflow-y-auto pr-1'].join(' ')}>
            {selectedStrokeShapeIndex >= 0 && (
              <div className="rounded-lg border border-gray-200 bg-white p-2 shadow-sm">
                <div className="mb-2 text-[11px] font-medium uppercase tracking-wide text-gray-500">
                  Line Weight
                </div>
                <div className="flex flex-col gap-1">
                  {STROKE_WEIGHT_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => handleStrokeWidthChange(option.id)}
                      className={[
                        'rounded border px-2 py-1 text-left text-xs transition-colors',
                        selectedStrokeWidth === option.id
                          ? 'border-sky-600 bg-sky-50 text-sky-700'
                          : 'border-gray-300 text-gray-600 hover:bg-gray-50',
                      ].join(' ')}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {selectedAngleIndex >= 0 && (
              <div className="rounded-lg border border-gray-200 bg-white p-2 shadow-sm">
                <div className="mb-2 text-[11px] font-medium uppercase tracking-wide text-gray-500">
                  Angle
                </div>
                <div className="mb-2 text-[11px] font-medium text-gray-500">Radius</div>
                <div className="grid grid-cols-2 gap-1">
                  {ANGLE_RADIUS_OPTIONS.map((radius) => (
                    <button
                      key={radius}
                      type="button"
                      onClick={() => handleAngleRadiusChange(radius)}
                      className={[
                        'rounded border px-2 py-1 text-left text-xs transition-colors',
                        Math.round(selectedAngleRadius) === radius
                          ? 'border-sky-600 bg-sky-50 text-sky-700'
                          : 'border-gray-300 text-gray-600 hover:bg-gray-50',
                      ].join(' ')}
                    >
                      {radius}
                    </button>
                  ))}
                </div>
                <label className="mt-2 block text-[11px] font-medium text-gray-500" htmlFor="geometry-angle-radius">
                  Custom radius
                </label>
                <input
                  id="geometry-angle-radius"
                  type="number"
                  min="8"
                  max="200"
                  step="1"
                  value={Math.round(selectedAngleRadius)}
                  onChange={(event) => handleAngleRadiusChange(event.target.value)}
                  className="mt-1 h-8 w-full rounded border border-gray-300 px-2 text-xs text-gray-700 outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-100"
                />

                <div className="mb-2 mt-3 text-[11px] font-medium text-gray-500">Shade</div>
                <div className="flex flex-col gap-1">
                  {ANGLE_SHADE_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => handleAngleShadeChange(option.id)}
                      className={[
                        'rounded border px-2 py-1 text-left text-xs transition-colors',
                        Math.abs(selectedAngleShade - option.id) < 0.01
                          ? 'border-sky-600 bg-sky-50 text-sky-700'
                          : 'border-gray-300 text-gray-600 hover:bg-gray-50',
                      ].join(' ')}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={handleAngleReflexToggle}
                  className={[
                    'mt-3 w-full rounded border px-2 py-1 text-left text-xs transition-colors',
                    selectedAngleUseReflex
                      ? 'border-sky-600 bg-sky-50 text-sky-700'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-50',
                  ].join(' ')}
                >
                  Reflex angle
                </button>
              </div>
            )}
            {selectedLineIndex >= 0 && (
              <div className="rounded-lg border border-gray-200 bg-white p-2 shadow-sm">
                <div className="mb-2 text-[11px] font-medium uppercase tracking-wide text-gray-500">
                  Line Style
                </div>
                <div className="flex flex-col gap-1">
                  {LINE_STYLE_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => handleLineStyleChange(option.id)}
                      className={[
                        'rounded border px-2 py-1 text-left text-xs transition-colors',
                        selectedLineStyle === option.id
                          ? 'border-sky-600 bg-sky-50 text-sky-700'
                          : 'border-gray-300 text-gray-600 hover:bg-gray-50',
                      ].join(' ')}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>

                <div className="mb-2 mt-3 text-[11px] font-medium uppercase tracking-wide text-gray-500">
                  Arrows
                </div>
                <div className="flex flex-col gap-1">
                  {LINE_ARROW_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => handleLineArrowChange(option.id)}
                      className={[
                        'rounded border px-2 py-1 text-left text-xs transition-colors',
                        selectedLineArrowMode === option.id
                          ? 'border-sky-600 bg-sky-50 text-sky-700'
                          : 'border-gray-300 text-gray-600 hover:bg-gray-50',
                      ].join(' ')}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>

                <div className="mb-2 mt-3 text-[11px] font-medium uppercase tracking-wide text-gray-500">
                  Segment Marks
                </div>
                <div className="flex flex-col gap-1">
                  {LINE_MARKING_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => handleLineMarkingChange(option.id)}
                      className={[
                        'rounded border px-2 py-1 text-left text-xs transition-colors',
                        selectedLineMarking === option.id
                          ? 'border-sky-600 bg-sky-50 text-sky-700'
                          : 'border-gray-300 text-gray-600 hover:bg-gray-50',
                      ].join(' ')}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>

                <div className="mb-2 mt-3 text-[11px] font-medium uppercase tracking-wide text-gray-500">
                  Parallel Marks
                </div>
                <div className="flex flex-col gap-1">
                  {LINE_PARALLEL_MARKER_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => handleLineParallelMarkerChange(option.id)}
                      className={[
                        'rounded border px-2 py-1 text-left text-xs transition-colors',
                        selectedLineParallelMarker === option.id
                          ? 'border-sky-600 bg-sky-50 text-sky-700'
                          : 'border-gray-300 text-gray-600 hover:bg-gray-50',
                      ].join(' ')}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={() => setShowGrid((v) => !v)}
              className={[
                'w-10 h-10 rounded-lg border flex items-center justify-center',
                showGrid ? 'border-sky-600 bg-sky-50 text-sky-700' : 'border-gray-300 text-gray-600',
              ].join(' ')}
              title="Toggle Grid"
            >
              <Grid3X3 className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setShowAxes((v) => !v)}
              className={[
                'w-10 h-10 rounded-lg border flex items-center justify-center',
                showAxes ? 'border-sky-600 bg-sky-50 text-sky-700' : 'border-gray-300 text-gray-600',
              ].join(' ')}
              title="Toggle Axes"
            >
              <LineChart className="w-4 h-4" />
            </button>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={handleUndo}
                disabled={!history.length}
                className="w-10 h-10 rounded-lg border border-gray-300 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
                title="Undo"
              >
                <Undo2 className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={handleRedo}
                disabled={!future.length}
                className="w-10 h-10 rounded-lg border border-gray-300 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
                title="Redo"
              >
                <Redo2 className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={!canDelete}
                className="w-10 h-10 rounded-lg border border-gray-300 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={handleDuplicateSelection}
                disabled={readOnly || !canDuplicate}
                className="w-10 h-10 rounded-lg border border-gray-300 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
                title="Duplicate (Ctrl+D)"
              >
                <CopyPlus className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={handleClear}
                className="w-10 h-10 rounded-lg border border-gray-300 text-gray-600 flex items-center justify-center"
                title="Clear"
              >
                <Eraser className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={handleExport}
                className="w-10 h-10 rounded-lg border border-gray-300 text-gray-600 flex items-center justify-center col-span-2"
                title="Export PNG"
              >
                <Download className="w-4 h-4" />
              </button>
            </div>
          </div>
          )}
        </div>
      </div>

      {isLibraryOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {libraryTab === 'shared' ? 'Shared diagrams' : 'Saved diagrams'}
                </h3>
                <p className="text-sm text-gray-500">
                  {libraryTab === 'shared'
                    ? 'View diagrams shared with you in read-only mode.'
                    : user?.uid
                      ? 'Open or update diagrams saved to your account.'
                      : 'Open or update diagrams saved in this browser.'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsLibraryOpen(false)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-300 text-gray-500 transition-colors hover:bg-gray-50"
                aria-label="Close saved diagrams"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            {user?.uid && (
              <div className="flex gap-2 border-b border-gray-200 px-5 py-3">
                <button
                  type="button"
                  onClick={() => setLibraryTab('saved')}
                  className={[
                    'rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    libraryTab === 'saved' ? 'bg-sky-50 text-sky-700' : 'text-gray-600 hover:bg-gray-50',
                  ].join(' ')}
                >
                  Saved by me
                </button>
                <button
                  type="button"
                  onClick={() => setLibraryTab('shared')}
                  className={[
                    'rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    libraryTab === 'shared' ? 'bg-sky-50 text-sky-700' : 'text-gray-600 hover:bg-gray-50',
                  ].join(' ')}
                >
                  Shared with me
                </button>
              </div>
            )}
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {isLibraryLoading ? (
                <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
                  Loading diagrams...
                </div>
              ) : libraryTab === 'shared' ? (
                safeSharedDiagrams.length ? (
                  <div className="space-y-3">
                    {safeSharedDiagrams.map((diagram) => (
                      <div key={diagram.id} className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 px-4 py-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-gray-900">{diagram.title}</p>
                          <p className="text-xs text-gray-500">
                            Shared by {diagram.ownerName}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => diagram.linkToken && window.location.assign(`/shared/${diagram.linkToken}`)}
                            disabled={!diagram.linkToken}
                            className="inline-flex items-center rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm font-medium text-sky-700 transition-colors hover:bg-sky-100 disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            View
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
                    No shared diagrams yet.
                  </div>
                )
              ) : safeSavedDiagrams.length ? (
                <div className="space-y-3">
                  {safeSavedDiagrams.map((diagram) => (
                    <div key={diagram.id} className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 px-4 py-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-gray-900">{diagram.title}</p>
                        <p className="text-xs text-gray-500">
                          Updated {diagram.updatedAt instanceof Date && !Number.isNaN(diagram.updatedAt.valueOf())
                            ? diagram.updatedAt.toLocaleString()
                            : 'recently'}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => loadDiagram(diagram.id)}
                          className="inline-flex items-center rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm font-medium text-sky-700 transition-colors hover:bg-sky-100"
                        >
                          Open
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteDiagram(diagram.id)}
                          className="inline-flex items-center rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
                  No saved diagrams yet.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <ShareDocumentModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        document={{
          id: currentDiagramId,
          name: diagramTitle || DEFAULT_DIAGRAM_TITLE,
          contentType: 'geometry-diagram',
          toolType: 'geometry-tool',
        }}
        readOnlyOnly
        titleLabel="Diagram"
        createAccessTokenForUserShare
      />
    </div>
  );
}

export default GeometryTool;
